import * as THREE from 'three';
import { initRenderer } from './renderer/setup';
import type { GTAORenderPipeline } from './renderer/gtao';
import {
  createReferenceLighting,
  prepareReferenceAreaLightLtc,
  type ReferenceLightingQuality,
} from './renderer/referenceLighting';
import { DebugOverlay } from './renderer/debugOverlay';
import { PhysicsEngine, type JointReadbackState, type RigidBodyReadbackState } from './physics/PhysicsEngine';
import { createBoxSpawner, type BoxSpawner, type EmitterRuntimeConfig } from './scene/boxSpawner';
import { createTrackedBodyVisualSet } from './scene/trackedBodyVisuals';
import { createTrackedSpringVisualSet } from './scene/trackedSpringVisuals';
import { buildClothBoxesDemo } from './scene/clothBoxesDemo';
import { buildBunnySoftBodyDemo } from './scene/bunnySoftBodyDemo';
import { buildColiseumDemo } from './scene/coliseumDemo';
import {
  COLISEUM_PRODUCTION_PHYSICS_MANIFESTS,
  type ColiseumProductionPhysicsManifest,
} from './scene/coliseumLayout';
import {
  buildDominoAdvancedDemo,
  buildDominoDemo,
  DOMINO_ADVANCED_PRODUCTION_SOLVER_ITERATIONS,
  DOMINO_PRODUCTION_SOLVER_ITERATIONS,
} from './scene/dominoDemo';
import { buildJointedSandboxDemo } from './scene/jointedSandboxDemo';
import { buildNewtonsCradleDemo } from './scene/newtonsCradleDemo';
import { buildRagdollAvalancheDemo } from './scene/ragdollAvalancheDemo';
import { buildRagdollCarouselDemo } from './scene/ragdollCarouselDemo';
import { buildRagdollClothDemo } from './scene/ragdollClothDemo';
import { buildRopeDemo } from './scene/ropeDemo';
import { buildSoftBodyDemo } from './scene/softBodyDemo';
import {
  buildWaterfallGutterDemo,
  WATERFALL_GUTTER_PRODUCTION_MANIFEST,
  WATERFALL_GUTTER_STATIC_BODY_COUNT,
} from './scene/waterfallGutterDemo';
import { ContactPointOverlay } from './scene/contactPointOverlay';
import { EmitterPanel, type EmitterResetDefaults, type StackPreset } from './scene/emitterPanel';
import { FPSController, type FPSControllerState } from './scene/fpsController';
import { GpuPassTimer } from './renderer/gpuPassTimer';
import { AVBD_FRICTION_STATIC } from './physics/avbdParams';
import type { ContactPoolMode } from './physics/types';
import {
  applyAvbdProfile,
  AVBD_DEFAULT_PROFILE_ID,
  getAvbdProfile,
  isAvbdProfileId,
  type AvbdProfileId,
} from './app/avbdReferenceConfig';
import { getGPUQueueSubmitObserver } from './physics/gpu/gpuQueueSubmitObserver';

type StackPresetConfig = {
  maxBodies: number;
  /** Render/emitter capacity is independent from the physics body ceiling. */
  spawnerCapacity?: number;
  initialCount: number;
  cameraPosition: [number, number, number];
  cameraLookAt: [number, number, number];
  physics: {
    substeps?: number;
    maxPairsPerBodyBroadphase: number;
    maxPersistentManifolds?: number;
    maxContactsPerBodySolver?: number;
    maxPairSolveColorCount?: number;
    activePairSolveColorCount?: number;
    pairManifoldSlots?: number;
    contactPoolMode?: ContactPoolMode;
    compactContactPoolCapacity?: number;
    solverIterations?: number;
    avbdAdaptiveIterations?: boolean;
    avbdAdaptiveMinIterations?: number;
    avbdAdaptiveDualTolerance?: number;
    avbdAdaptivePenaltyTolerance?: number;
    avbdPenaltyDecayGamma?: number;
    avbdCacheContactTangentInvLength?: boolean;
    avbdDenseContactSolverState?: boolean;
    avbdSpecializeReferenceFinalizer?: boolean;
    avbdCaptureBodyDeltaCache?: boolean;
    avbdPackedContactSolverGeometry?: boolean;
    avbdFuseFinalCaptureCommit?: boolean;
    avbdManifoldCapture?: boolean;
    avbdCooperativeBodySolve?: boolean;
    enableSleeping?: boolean;
    enableSleepingSparseBroadphase?: boolean;
    sleepingSparseMinimumBodyCount?: number;
    sleepFrames?: number;
    sleepLinearSpeed?: number;
    sleepAngularSpeed?: number;
    enableBroadphasePairDedup?: boolean;
    maxJoints?: number;
    maxSprings?: number;
    maxIgnoredPairs?: number;
  };
  layout: 'grid' | 'pyramid-wall';
  grid?: {
    x: number;
    z: number;
    startY: number;
    sizeX: number;
    sizeY: number;
    sizeZ: number;
    spacing: number;
    spacingX?: number;
    spacingY?: number;
    spacingZ?: number;
    stacksX?: number;
    stacksZ?: number;
    stackSpacingX?: number;
    stackSpacingZ?: number;
  };
  pyramidWall?: {
    x: number;
    z: number;
    startY: number;
    layers: number;
    stepX?: number;
    stepY?: number;
  };
};

function createColiseumPresetPhysicsConfig(
  manifest: ColiseumProductionPhysicsManifest,
): StackPresetConfig['physics'] {
  return {
    substeps: manifest.substeps,
    maxPairsPerBodyBroadphase: manifest.maxPairsPerBodyBroadphase,
    maxPersistentManifolds: manifest.maxPersistentManifolds,
    pairManifoldSlots: manifest.pairManifoldSlots,
    contactPoolMode: manifest.contactPoolMode,
    compactContactPoolCapacity: manifest.compactContactPoolCapacity,
    maxContactsPerBodySolver: manifest.maxContactsPerBodySolver,
    maxPairSolveColorCount: manifest.pairSolveColorCount,
    maxJoints: manifest.maxJoints,
    maxSprings: manifest.maxSprings,
    maxIgnoredPairs: manifest.maxIgnoredPairs,
    solverIterations: manifest.solverIterations,
    avbdAdaptiveIterations: manifest.avbdAdaptiveIterations,
    avbdAdaptiveMinIterations: manifest.avbdAdaptiveMinIterations,
    avbdAdaptiveDualTolerance: manifest.avbdAdaptiveDualTolerance,
    avbdAdaptivePenaltyTolerance: manifest.avbdAdaptivePenaltyTolerance,
    avbdPenaltyDecayGamma: manifest.avbdPenaltyDecayGamma,
    avbdCacheContactTangentInvLength: manifest.avbdCacheContactTangentInvLength,
    avbdDenseContactSolverState: manifest.avbdDenseContactSolverState,
    avbdSpecializeReferenceFinalizer: manifest.avbdSpecializeReferenceFinalizer,
    avbdCaptureBodyDeltaCache: manifest.avbdCaptureBodyDeltaCache,
    avbdPackedContactSolverGeometry: manifest.avbdPackedContactSolverGeometry,
    avbdFuseFinalCaptureCommit: manifest.avbdFuseFinalCaptureCommit,
    avbdManifoldCapture: manifest.avbdManifoldCapture,
    avbdCooperativeBodySolve: manifest.avbdCooperativeBodySolve,
    enableSleeping: manifest.enableSleeping,
    enableSleepingSparseBroadphase: manifest.enableSleepingSparseBroadphase,
    sleepingSparseMinimumBodyCount: manifest.sleepingSparseMinimumBodyCount,
    sleepFrames: manifest.sleepFrames,
    sleepLinearSpeed: manifest.sleepLinearSpeed,
    sleepAngularSpeed: manifest.sleepAngularSpeed,
    enableBroadphasePairDedup: manifest.enableBroadphasePairDedup,
  };
}

type MiniBridgeJointDescriptor = {
  joint: number;
  label: string;
  bodyA: number | null;
  bodyB: number;
  anchorA: [number, number, number];
  anchorB: [number, number, number];
};

type MiniBridgeDiagnosticsConfig = {
  preset: 'bridge-demo' | 'bridge-demo-empty' | 'bridge-demo-fixed';
  centerBody: number;
  joints: MiniBridgeJointDescriptor[];
};

type CarouselDiagnosticsConfig = {
  joints: MiniBridgeJointDescriptor[];
};

type SyncedSceneVisual = {
  syncVisuals: (engine: PhysicsEngine) => void;
  dispose: () => void;
};

type ProjectileShootConfig = {
  position: [number, number, number];
  direction: [number, number, number];
  averageSpeed: number;
  speedJitter?: number;
  angularJitter?: number;
};

type WebphysDebugWindow = typeof window & {
  __WEBPHYS_CAMERA_DEBUG__?: {
    capture: () => FPSControllerState;
    isLocked: () => boolean;
  };
};

type SceneRuntime = {
  sceneRoot: THREE.Group;
  spawner: BoxSpawner;
  defaultEmitter: EmitterRuntimeConfig;
  trackedVisualSets: Array<ReturnType<typeof createTrackedBodyVisualSet>>;
  trackedSpringVisualSets: Array<ReturnType<typeof createTrackedSpringVisualSet>>;
  syncedSceneVisuals: SyncedSceneVisual[];
  sceneUpdateCallbacks: Array<(dt: number) => void>;
  contactPointOverlay: ContactPointOverlay;
  bridgeMiniDiagnostics: MiniBridgeDiagnosticsConfig | null;
  carouselDiagnostics: CarouselDiagnosticsConfig | null;
  sceneStatusText: (() => string | null) | null;
  shootProjectileOverride: ((config: ProjectileShootConfig) => boolean) | null;
  dispose: () => void;
};

function multiplyQuat4(
  a: [number, number, number, number],
  b: [number, number, number, number],
): [number, number, number, number] {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

function conjugateQuat4(
  q: [number, number, number, number],
): [number, number, number, number] {
  return [-q[0], -q[1], -q[2], q[3]];
}

function normalizeQuat4(
  q: [number, number, number, number],
): [number, number, number, number] {
  const length = Math.hypot(q[0], q[1], q[2], q[3]);
  if (length <= 1e-12) return [0.0, 0.0, 0.0, 1.0];
  const invLength = 1.0 / length;
  return [q[0] * invLength, q[1] * invLength, q[2] * invLength, q[3] * invLength];
}

function rotateLocalPoint(
  point: [number, number, number],
  quaternion: [number, number, number, number],
): [number, number, number] {
  const [qx, qy, qz, qw] = normalizeQuat4(quaternion);
  const [vx, vy, vz] = point;
  const ix =  qw * vx + qy * vz - qz * vy;
  const iy =  qw * vy + qz * vx - qx * vz;
  const iz =  qw * vz + qx * vy - qy * vx;
  const iw = -qx * vx - qy * vy - qz * vz;

  return [
    ix * qw + iw * -qx + iy * -qz - iz * -qy,
    iy * qw + iw * -qy + iz * -qx - ix * -qz,
    iz * qw + iw * -qz + ix * -qy - iy * -qx,
  ];
}

function jointAnchorError(
  statesByBody: Map<number, RigidBodyReadbackState>,
  joint: MiniBridgeJointDescriptor,
): number {
  const stateB = statesByBody.get(joint.bodyB);
  if (!stateB) return Infinity;
  const anchorBOffset = rotateLocalPoint(joint.anchorB, stateB.quaternion);
  const worldAnchorB: [number, number, number] = [
    stateB.position[0] + anchorBOffset[0],
    stateB.position[1] + anchorBOffset[1],
    stateB.position[2] + anchorBOffset[2],
  ];

  let worldAnchorA: [number, number, number];
  if (joint.bodyA === null) {
    worldAnchorA = joint.anchorA;
  } else {
    const stateA = statesByBody.get(joint.bodyA);
    if (!stateA) return Infinity;
    const anchorAOffset = rotateLocalPoint(joint.anchorA, stateA.quaternion);
    worldAnchorA = [
      stateA.position[0] + anchorAOffset[0],
      stateA.position[1] + anchorAOffset[1],
      stateA.position[2] + anchorAOffset[2],
    ];
  }

  return Math.hypot(
    worldAnchorA[0] - worldAnchorB[0],
    worldAnchorA[1] - worldAnchorB[1],
    worldAnchorA[2] - worldAnchorB[2],
  );
}

function currentJointAngularError(
  qA: [number, number, number, number],
  qB: [number, number, number, number],
  restRelative: [number, number, number, number],
): [number, number, number] {
  const worldQA = normalizeQuat4(qA);
  const currentQB = normalizeQuat4(qB);
  const currentRelative = normalizeQuat4(multiplyQuat4(conjugateQuat4(worldQA), currentQB));
  let delta = multiplyQuat4(currentRelative, conjugateQuat4(normalizeQuat4(restRelative)));
  if (delta[3] < 0.0) {
    delta = [-delta[0], -delta[1], -delta[2], -delta[3]];
  }
  return [2.0 * delta[0], 2.0 * delta[1], 2.0 * delta[2]];
}

function demo3dStyleJointAngularError(
  qA: [number, number, number, number],
  qB: [number, number, number, number],
): [number, number, number] {
  const delta = multiplyQuat4(normalizeQuat4(qA), conjugateQuat4(normalizeQuat4(qB)));
  return [2.0 * delta[0], 2.0 * delta[1], 2.0 * delta[2]];
}

function vectorMagnitude3(value: [number, number, number]): number {
  return Math.hypot(value[0], value[1], value[2]);
}

function formatTriple(value: [number, number, number], digits = 3): string {
  return `[${value[0].toFixed(digits)}, ${value[1].toFixed(digits)}, ${value[2].toFixed(digits)}]`;
}

const STACK_PRESET_QUERY_KEY = 'stackPreset';
const AVBD_PROFILE_QUERY_KEY = 'avbdProfile';
const AVBD_STORED_TUNING_QUERY_KEY = 'avbdStoredTuning';
const AVBD_FIXED_ITERATIONS_QUERY_KEY = 'avbdFixedIterations';
const PERF_EMIT_FRAME_QUERY_KEY = 'perfEmitFrame';
const PERF_EMIT_COUNT_QUERY_KEY = 'perfEmitCount';
const DEBUG_TIMING_STORAGE_KEY = 'webphys_debug_timing';
const DEBUG_BEHAVIOR_STORAGE_KEY = 'webphys_debug_behavior';
const SHOW_CONTACT_POINTS_STORAGE_KEY = 'webphys_show_contact_points';
const SHADOWS_ENABLED_STORAGE_KEY = 'webphys_shadows_enabled';
// v2 intentionally does not inherit the old HBAO/GTAO off bit: GTAO is now a
// default presentation feature for every test scene, with a separate scalable
// dense-scene profile. Users can still override it explicitly after migration.
const GTAO_ENABLED_STORAGE_KEY = 'webphys_gtao_enabled_v2';
const GTAO_INTENSITY_STORAGE_KEY = 'webphys_gtao_intensity';
const GTAO_RADIUS_STORAGE_KEY = 'webphys_gtao_radius';
const LEGACY_AO_INTENSITY_STORAGE_KEY = 'webphys_hbao_intensity';
const LEGACY_AO_RADIUS_STORAGE_KEY = 'webphys_hbao_radius';
// Pause is a transient transport control, not a durable user preference.  A
// value written by an older build used to survive scene changes and deploys;
// on a large scene that made the fresh page look permanently frozen before
// its first true step.  Keep the key only so startup can remove that legacy
// state once.
const LEGACY_SIMULATION_PAUSED_STORAGE_KEY = 'webphys_simulation_paused';
const LOG_EVERY_STEP_STORAGE_KEY = 'webphys_log_every_step';
// Version the official-demo3d tuning keys so values saved by an older
// profile cannot silently override the currently certified defaults.
const DUAL_UPDATE_BETA_STORAGE_KEY = 'webphys_demo3d2026_v1_dual_update_beta';
const ANGULAR_DUAL_UPDATE_BETA_STORAGE_KEY =
  'webphys_demo3d2026_v1_angular_dual_update_beta';
const REGULARIZATION_ALPHA_STORAGE_KEY = 'webphys_demo3d2026_v1_regularization_alpha';
const PENALTY_GAMMA_STORAGE_KEY = 'webphys_demo3d2026_v1_penalty_gamma';
const AVBD_PENALTY_FLOOR_STORAGE_KEY = 'webphys_demo3d2026_v1_avbd_penalty_floor';
const SOLVER_ITERATIONS_STORAGE_KEY = 'webphys_solver_iterations';
const DEFAULT_STACK_PRESET: StackPreset = 'single-5';
const COLISEUM_50K_PRODUCTION_PHYSICS =
  COLISEUM_PRODUCTION_PHYSICS_MANIFESTS['coliseum-50k'];
const COLISEUM_64K_PRODUCTION_PHYSICS =
  COLISEUM_PRODUCTION_PHYSICS_MANIFESTS['coliseum-64k'];
const STACK_PRESET_CONFIGS: Record<StackPreset, StackPresetConfig> = {
  'single-5': {
    maxBodies: 2048,
    initialCount: 5 * 5 * 5,
    cameraPosition: [0, 3.2, 10],
    cameraLookAt: [0, 1.5, 0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.2995,
      sizeX: 5,
      sizeY: 5,
      sizeZ: 5,
      spacing: 0.01,
      spacingY: -0.0005,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'single-10': {
    // The scene contains 1,000 dynamic boxes plus one floor.  The old 8,192
    // capacity allocated and uploaded a ~288 MiB contact pool before the
    // first frame. Keep useful emitter headroom without an 8x over-allocation.
    maxBodies: 1280,
    initialCount: 10 * 10 * 10,
    cameraPosition: [0, 5.0, 16],
    cameraLookAt: [0, 3.0, 0],
    physics: {
      // Horizontal spacing removes edge/corner ghost pairs; 32 candidates
      // still leave ample fat-AABB and transient-motion headroom.
      maxPairsPerBodyBroadphase: 32,
      maxContactsPerBodySolver: 64,
      // The resting grid graph is bipartite. Four strict GS colors leave
      // headroom for transient box/projectile contacts while avoiding four
      // unnecessary strict-color dispatches in every one of the 28 AVBD iterations.
      // Any graph conflict still goes through the race-free Jacobi fallback.
      maxPairSolveColorCount: 4,
      pairManifoldSlots: 8,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
      enableSleeping: true,
      // Keep the certified 28-pass quality ceiling while allowing a quiet
      // stack to stop only after two converged rounds at or beyond pass 24.
      avbdAdaptiveIterations: true,
      avbdAdaptiveMinIterations: 24,
      // 24 passes only narrowly clears the 1,800-step layer-drift gate, while
      // 16 fails it. Twenty-eight remains the hard fail-open maximum; adaptive
      // residual violations therefore retain the previously certified quality.
      solverIterations: 28,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      // Start at the reference 0.5 mm penetration slop so the zero-load C0 is
      // already satisfied without a visible free-fall or centimetre settling.
      startY: 0.2995,
      sizeX: 10,
      sizeY: 10,
      sizeZ: 10,
      spacing: 0.01,
      spacingY: -0.0005,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'wall-10x10x1': {
    maxBodies: 4096,
    initialCount: 10 * 10 * 1,
    cameraPosition: [0, 4.0, 14.0],
    cameraLookAt: [0, 2.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 10,
      sizeY: 10,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'line-2x1x1': {
    maxBodies: 256,
    initialCount: 2,
    cameraPosition: [0, 1.8, 5.0],
    cameraLookAt: [0, 0.9, 0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 2,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'line-1x1x1': {
    maxBodies: 256,
    initialCount: 1,
    cameraPosition: [0, 1.4, 4.2],
    cameraLookAt: [0, 0.55, 0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'line-3x1x1': {
    maxBodies: 256,
    initialCount: 3,
    cameraPosition: [0, 2.2, 5.5],
    cameraLookAt: [0, 1.2, 0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 3,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'line-10x1x1': {
    maxBodies: 1024,
    initialCount: 10 * 1 * 1,
    cameraPosition: [0, 4.6, 9.0],
    cameraLookAt: [0, 2.7, 0],
    physics: {
      maxPairsPerBodyBroadphase: 32,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 10,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'bridge-demo': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [0, 16.0, 34.0],
    cameraLookAt: [0, 8.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 64,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'bridge-demo-empty': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [0, 16.0, 34.0],
    cameraLookAt: [0, 8.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 32,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'bridge-demo-fixed': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [0, 16.0, 34.0],
    cameraLookAt: [0, 8.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 64,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'soft-body': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [14.0, 18.0, 28.0],
    cameraLookAt: [0.0, 13.0, 0.0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'bunny-soft-body': {
    maxBodies: 12288,
    initialCount: 0,
    cameraPosition: [14.0, 12.0, 22.0],
    cameraLookAt: [0.0, 7.0, 0.0],
    physics: {
      maxPairsPerBodyBroadphase: 96,
      maxContactsPerBodySolver: 192,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'spring-demo': {
    maxBodies: 320,
    initialCount: 0,
    cameraPosition: [0, 28.0, 58.0],
    cameraLookAt: [0, 18.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 24,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'spring-ratio': {
    maxBodies: 256,
    initialCount: 0,
    cameraPosition: [0, 17.5, 22.0],
    cameraLookAt: [0, 17.5, 0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'rope-demo': {
    maxBodies: 256,
    initialCount: 0,
    cameraPosition: [0, 16.0, 28.0],
    cameraLookAt: [0, 12.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 24,
      maxContactsPerBodySolver: 96,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'heavy-rope-demo': {
    maxBodies: 256,
    initialCount: 0,
    cameraPosition: [0, 19.0, 32.0],
    cameraLookAt: [0, 15.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 24,
      maxContactsPerBodySolver: 96,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'sphere-demo': {
    maxBodies: 256,
    initialCount: 0,
    cameraPosition: [0, 7.0, 18.0],
    cameraLookAt: [0, 3.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'ragdoll-cloth-demo': {
    maxBodies: 4096,
    initialCount: 0,
    cameraPosition: [0, 24.0, 46.0],
    cameraLookAt: [0, 12.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'ragdoll-carousel-demo': {
    maxBodies: 4096,
    initialCount: 0,
    cameraPosition: [0, 22.0, 42.0],
    cameraLookAt: [0, 15.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'jointed-sandbox-demo': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [0, 12.0, 56.0],
    cameraLookAt: [0, 6.5, 0.0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'cloth-boxes-demo': {
    maxBodies: 1024,
    initialCount: 0,
    cameraPosition: [0, 11.0, 18.0],
    cameraLookAt: [0, 7.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 32,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 12,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'coliseum-50k': {
    maxBodies: COLISEUM_50K_PRODUCTION_PHYSICS.maxBodies,
    spawnerCapacity: 1,
    initialCount: 0,
    cameraPosition: [48.0, 12.0, 48.0],
    cameraLookAt: [0.0, 5.0, 0.0],
    // The manifest's one-substep transaction is the exact path certified by
    // both the settled and projectile production regressions.
    physics: createColiseumPresetPhysicsConfig(COLISEUM_50K_PRODUCTION_PHYSICS),
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'coliseum-64k': {
    maxBodies: COLISEUM_64K_PRODUCTION_PHYSICS.maxBodies,
    spawnerCapacity: 1,
    initialCount: 0,
    cameraPosition: [52.0, 13.0, 52.0],
    cameraLookAt: [0.0, 5.5, 0.0],
    physics: createColiseumPresetPhysicsConfig(COLISEUM_64K_PRODUCTION_PHYSICS),
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'pyramid-8': {
    maxBodies: 2048,
    initialCount: (8 * 9) / 2,
    cameraPosition: [0, 3.8, 11.0],
    cameraLookAt: [0, 1.5, 0],
    physics: {
      maxPairsPerBodyBroadphase: 48,
      maxContactsPerBodySolver: 128,
      maxPairSolveColorCount: 16,
    },
    layout: 'pyramid-wall',
    pyramidWall: {
      x: 0,
      z: 0,
      startY: 0.3,
      layers: 8,
      stepX: 0.63,
      stepY: 0.51,
    },
  },
  'waterfall-gutter': {
    // The emitter owns exactly 2,048 dynamic slots; the shared production
    // gutter builder adds 16 static bodies. Derive physics capacity from that
    // real manifest instead of reserving 240 unreachable body slots in every
    // broadphase/constraint buffer.
    maxBodies: WATERFALL_GUTTER_PRODUCTION_MANIFEST.dynamicCapacity
      + WATERFALL_GUTTER_STATIC_BODY_COUNT,
    spawnerCapacity: WATERFALL_GUTTER_PRODUCTION_MANIFEST.dynamicCapacity,
    initialCount: WATERFALL_GUTTER_PRODUCTION_MANIFEST.initialDynamicBodyCount,
    cameraPosition: [10.0, 30.0, 72.0],
    cameraLookAt: [8.0, 16.0, -8.0],
    physics: {
      maxPairsPerBodyBroadphase: WATERFALL_GUTTER_PRODUCTION_MANIFEST.maxPairsPerBodyBroadphase,
      maxPersistentManifolds: WATERFALL_GUTTER_PRODUCTION_MANIFEST.maxPersistentManifolds,
      pairManifoldSlots: WATERFALL_GUTTER_PRODUCTION_MANIFEST.pairManifoldSlots,
      contactPoolMode: WATERFALL_GUTTER_PRODUCTION_MANIFEST.contactPoolMode,
      compactContactPoolCapacity: WATERFALL_GUTTER_PRODUCTION_MANIFEST.compactContactPoolCapacity,
      maxContactsPerBodySolver: WATERFALL_GUTTER_PRODUCTION_MANIFEST.maxContactsPerBodySolver,
      maxPairSolveColorCount: WATERFALL_GUTTER_PRODUCTION_MANIFEST.pairSolveColorCount,
      maxJoints: 0,
      maxSprings: 0,
      maxIgnoredPairs: 0,
      solverIterations: WATERFALL_GUTTER_PRODUCTION_MANIFEST.solverIterations,
    },
    layout: 'grid',
    grid: {
      ...WATERFALL_GUTTER_PRODUCTION_MANIFEST.grid,
    },
  },
  'grid-1000x10': {
    maxBodies: 12288,
    initialCount: 1000 * 10,
    cameraPosition: [0, 26.0, 70.0],
    cameraLookAt: [0, 8.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 12,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 10,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 25,
      stacksZ: 40,
      stackSpacingX: 0.5,
      stackSpacingZ: 0.5,
    },
  },
  'grid-64x10': {
    maxBodies: 65536,
    initialCount: 64 * 10 * 10 * 10,
    cameraPosition: [0, 12.0, 36.0],
    cameraLookAt: [0, 6.0, 0],
    physics: {
      maxPairsPerBodyBroadphase: 12,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0,
      z: 0,
      startY: 0.3,
      sizeX: 10,
      sizeY: 10,
      sizeZ: 10,
      spacing: 0.01,
      stacksX: 8,
      stacksZ: 8,
      stackSpacingX: 0.5,
      stackSpacingZ: 0.5,
    },
  },
  'dominoes-demo': {
    maxBodies: 32768,
    initialCount: 0,
    cameraPosition: [0.0, 19.0, 78.0],
    cameraLookAt: [0.0, 5.5, 4.0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
      solverIterations: DOMINO_PRODUCTION_SOLVER_ITERATIONS,
    },
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'dominoes-advanced-demo': {
    maxBodies: 32768,
    initialCount: 0,
    cameraPosition: [0.0, 18.0, 58.0],
    cameraLookAt: [0.0, 8.0, 5.0],
    physics: {
      maxPairsPerBodyBroadphase: 16,
      maxContactsPerBodySolver: 64,
      maxPairSolveColorCount: 8,
      solverIterations: DOMINO_ADVANCED_PRODUCTION_SOLVER_ITERATIONS,
    },
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'newtons-cradles-demo': {
    // The scene contains 176 bodies. Keeping a small explicit headroom avoids
    // allocating the old 2,048-body contact/manifold buffers (~54 MiB for the
    // contact records alone) for a compact diagnostic scene.
    maxBodies: 256,
    initialCount: 0,
    cameraPosition: [0.0, 15.0, 68.0],
    cameraLookAt: [0.0, 7.5, -6.0],
    physics: {
      maxPairsPerBodyBroadphase: 8,
      maxContactsPerBodySolver: 8,
      maxPairSolveColorCount: 8,
      activePairSolveColorCount: 8,
    },
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
  'ragdoll-avalanche-demo': {
    maxBodies: 16384,
    initialCount: 0,
    cameraPosition: [78.0, 48.0, 0.0],
    cameraLookAt: [6.0, 34.0, 0.0],
    physics: {
      maxPairsPerBodyBroadphase: 24,
      maxContactsPerBodySolver: 40,
    },
    layout: 'grid',
    grid: {
      x: 0.0,
      z: 0.0,
      startY: 0.3,
      sizeX: 1,
      sizeY: 1,
      sizeZ: 1,
      spacing: 0.01,
      stacksX: 1,
      stacksZ: 1,
      stackSpacingX: 0.0,
      stackSpacingZ: 0.0,
    },
  },
};

// Allocate optional constraint families only for scenes that can actually
// create them.  Passing undefined used to reserve maxBodies*8 joints and
// maxBodies*12 springs even for pure contact stacks, duplicating large CPU/GPU
// buffers and making simple scene startup much more expensive than its work.
const JOINT_PRESETS = new Set<StackPreset>([
  'bridge-demo',
  'bridge-demo-empty',
  'bridge-demo-fixed',
  'soft-body',
  'bunny-soft-body',
  'rope-demo',
  'heavy-rope-demo',
  'ragdoll-cloth-demo',
  'ragdoll-carousel-demo',
  'jointed-sandbox-demo',
  'ragdoll-avalanche-demo',
]);
const SPRING_PRESETS = new Set<StackPreset>([
  'spring-demo',
  'spring-ratio',
  'ragdoll-cloth-demo',
  'cloth-boxes-demo',
  'newtons-cradles-demo',
]);

function isStackPreset(value: string | null): value is StackPreset {
  return value === 'single-5'
    || value === 'single-10'
    || value === 'wall-10x10x1'
    || value === 'line-1x1x1'
    || value === 'line-2x1x1'
    || value === 'line-3x1x1'
    || value === 'line-10x1x1'
    || value === 'bridge-demo'
    || value === 'bridge-demo-empty'
    || value === 'bridge-demo-fixed'
    || value === 'soft-body'
    || value === 'bunny-soft-body'
    || value === 'spring-demo'
    || value === 'spring-ratio'
    || value === 'rope-demo'
    || value === 'heavy-rope-demo'
    || value === 'sphere-demo'
    || value === 'ragdoll-cloth-demo'
    || value === 'ragdoll-carousel-demo'
    || value === 'jointed-sandbox-demo'
    || value === 'cloth-boxes-demo'
    || value === 'coliseum-50k'
    || value === 'coliseum-64k'
    || value === 'pyramid-8'
    || value === 'waterfall-gutter'
    || value === 'grid-1000x10'
    || value === 'grid-64x10'
    || value === 'dominoes-demo'
    || value === 'dominoes-advanced-demo'
    || value === 'newtons-cradles-demo'
    || value === 'ragdoll-avalanche-demo';
}

function getStackPresetFromUrl(): StackPreset {
  const value = new URLSearchParams(window.location.search).get(STACK_PRESET_QUERY_KEY);
  return isStackPreset(value) ? value : DEFAULT_STACK_PRESET;
}

function getAvbdProfileRequestFromUrl(): {
  id: AvbdProfileId;
  explicit: boolean;
} {
  const value = new URLSearchParams(window.location.search).get(AVBD_PROFILE_QUERY_KEY);
  return {
    id: isAvbdProfileId(value) ? value : AVBD_DEFAULT_PROFILE_ID,
    explicit: value !== null && isAvbdProfileId(value),
  };
}

function getStoredAvbdTuningOptInFromUrl(): boolean {
  return new URLSearchParams(window.location.search).get(AVBD_STORED_TUNING_QUERY_KEY) === '1';
}

function getFixedIterationsOverrideFromUrl(): boolean | null {
  const value = new URLSearchParams(window.location.search)
    .get(AVBD_FIXED_ITERATIONS_QUERY_KEY)
    ?.trim()
    .toLowerCase();
  if (value === '1' || value === 'true' || value === 'on') return true;
  if (value === '0' || value === 'false' || value === 'off') return false;
  return null;
}

function reloadWithStackPreset(preset: StackPreset): void {
  const url = new URL(window.location.href);
  url.searchParams.set(STACK_PRESET_QUERY_KEY, preset);
  // Scene selection is application state, not browser history. Replacing the
  // document prevents a previous hundreds-of-MiB WebGPU scene from remaining
  // as a back/forward-cache entry beside the newly selected scene.
  window.location.replace(url.toString());
}

function loadPerfEmitConfig(): { frame: number; count: number } | null {
  try {
    const url = new URL(window.location.href);
    const frameRaw = url.searchParams.get(PERF_EMIT_FRAME_QUERY_KEY);
    if (!frameRaw) return null;

    const frame = Math.max(0, Math.floor(Number(frameRaw)));
    if (!Number.isFinite(frame)) return null;

    const countRaw = url.searchParams.get(PERF_EMIT_COUNT_QUERY_KEY);
    const count = countRaw === null ? 16 : Math.max(1, Math.floor(Number(countRaw)));
    if (!Number.isFinite(count)) return null;

    return { frame, count };
  } catch {
    return null;
  }
}

function loadDebugTimingEnabled(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(DEBUG_TIMING_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistDebugTimingEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(DEBUG_TIMING_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadDebugBehaviorEnabled(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(DEBUG_BEHAVIOR_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistDebugBehaviorEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(DEBUG_BEHAVIOR_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadShowContactPoints(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(SHOW_CONTACT_POINTS_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistShowContactPoints(enabled: boolean): void {
  try {
    window.localStorage.setItem(SHOW_CONTACT_POINTS_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadShadowsEnabled(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(SHADOWS_ENABLED_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistShadowsEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(SHADOWS_ENABLED_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function readMigratedGtaoSetting(key: string, legacyKey: string): string | null {
  const current = window.localStorage.getItem(key);
  if (current !== null) return current;
  const legacy = window.localStorage.getItem(legacyKey);
  if (legacy !== null) window.localStorage.setItem(key, legacy);
  return legacy;
}

function loadGtaoEnabled(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(GTAO_ENABLED_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistGtaoEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(GTAO_ENABLED_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadGtaoScalar(
  key: string,
  legacyKey: string,
  defaultValue: number,
  min: number,
  max: number,
): number {
  try {
    const stored = readMigratedGtaoSetting(key, legacyKey);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    return Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function persistGtaoScalar(key: string, value: number, min: number, max: number): void {
  try {
    window.localStorage.setItem(key, String(Math.max(min, Math.min(max, value))));
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadSimulationPaused(defaultValue: boolean): boolean {
  const queryValue = new URLSearchParams(window.location.search).get('paused')
    ?.trim()
    .toLowerCase();
  const queryOverride = queryValue === '1' || queryValue === 'true' || queryValue === 'on'
    ? true
    : queryValue === '0' || queryValue === 'false' || queryValue === 'off'
      ? false
      : null;
  try {
    window.localStorage.removeItem(LEGACY_SIMULATION_PAUSED_STORAGE_KEY);
  } catch {
    // Ignore storage errors (private mode / blocked storage). Pause still
    // remains local to this page lifetime.
  }
  return queryOverride ?? defaultValue;
}

function loadLogEveryStep(defaultValue: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(LOG_EVERY_STEP_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const normalized = stored.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  } catch {
    return defaultValue;
  }
}

function persistLogEveryStep(enabled: boolean): void {
  try {
    window.localStorage.setItem(LOG_EVERY_STEP_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadDualUpdateBeta(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(DUAL_UPDATE_BETA_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(0.0, value);
  } catch {
    return defaultValue;
  }
}

function persistDualUpdateBeta(beta: number): void {
  try {
    const clamped = Math.max(0.0, beta);
    window.localStorage.setItem(DUAL_UPDATE_BETA_STORAGE_KEY, String(clamped));
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadRegularizationAlpha(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(REGULARIZATION_ALPHA_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(0.0, Math.min(1.0, value));
  } catch {
    return defaultValue;
  }
}

function persistRegularizationAlpha(alpha: number): void {
  try {
    const clamped = Math.max(0.0, Math.min(1.0, alpha));
    window.localStorage.setItem(REGULARIZATION_ALPHA_STORAGE_KEY, String(clamped));
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadPenaltyDecayGamma(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(PENALTY_GAMMA_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(0.0, Math.min(1.0, value));
  } catch {
    return defaultValue;
  }
}

function persistPenaltyDecayGamma(gamma: number): void {
  try {
    const clamped = Math.max(0.0, Math.min(1.0, gamma));
    window.localStorage.setItem(PENALTY_GAMMA_STORAGE_KEY, String(clamped));
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

function loadAvbdPenaltyFloor(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(AVBD_PENALTY_FLOOR_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(1e-6, value);
  } catch {
    return defaultValue;
  }
}

function loadSolverIterations(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(SOLVER_ITERATIONS_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(1, Math.min(64, Math.floor(value)));
  } catch {
    return defaultValue;
  }
}

function persistSolverIterations(iterations: number): void {
  try {
    const clamped = Math.max(1, Math.min(64, Math.floor(iterations)));
    window.localStorage.setItem(SOLVER_ITERATIONS_STORAGE_KEY, String(clamped));
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

async function main() {
  // Production-only startup telemetry. The epoch is main-entry so the values
  // include renderer/physics construction and scene population, while the
  // counters below only observe the existing animation/physics schedule.
  const startupStartedAt = performance.now();
  let startupFirstRenderMs: number | null = null;
  let startupFirstTrueSimulationStepMs: number | null = null;
  let startupFirstTenRafFramesObserved = 0;
  let startupFirstTenRafMaxGapMs = 0.0;
  let startupResponsiveRafFramesObserved = 0;
  let startupResponsiveRafMaxGapMs = 0.0;
  let startupPostReadyRafFramesObserved = 0;
  let startupPostReadyRafMaxGapMs = 0.0;
  let startupPhysicsReadyObserved = false;
  let startupTrueStepObserved = false;
  let startupPreviousRafAt: number | null = null;
  document.documentElement.dataset.startupFirstTenRafFramesObserved = '0';
  document.documentElement.dataset.startupFirstTenRafMaxGapMs = '0.000';
  document.documentElement.dataset.startupResponsiveRafFramesObserved = '0';
  document.documentElement.dataset.startupResponsiveRafMaxGapMs = '0.000';
  document.documentElement.dataset.startupPostReadyRafFramesObserved = '0';
  document.documentElement.dataset.startupPostReadyRafMaxGapMs = '0.000';
  document.documentElement.dataset.startupRafObserverComplete = 'false';
  const observeStartupRaf = (now: number): void => {
    let gapMs = 0.0;
    if (startupPreviousRafAt !== null) {
      gapMs = now - startupPreviousRafAt;
      startupResponsiveRafMaxGapMs = Math.max(startupResponsiveRafMaxGapMs, gapMs);
    }
    startupPreviousRafAt = now;
    startupResponsiveRafFramesObserved++;
    if (startupFirstTenRafFramesObserved < 10) {
      startupFirstTenRafMaxGapMs = Math.max(startupFirstTenRafMaxGapMs, gapMs);
      startupFirstTenRafFramesObserved++;
      document.documentElement.dataset.startupFirstTenRafFramesObserved = String(
        startupFirstTenRafFramesObserved,
      );
      document.documentElement.dataset.startupFirstTenRafMaxGapMs =
        startupFirstTenRafMaxGapMs.toFixed(3);
    }
    if (startupPhysicsReadyObserved && startupTrueStepObserved) {
      startupPostReadyRafFramesObserved++;
    }
    if (startupPhysicsReadyObserved) {
      startupPostReadyRafMaxGapMs = Math.max(startupPostReadyRafMaxGapMs, gapMs);
    }
    document.documentElement.dataset.startupResponsiveRafFramesObserved = String(
      startupResponsiveRafFramesObserved,
    );
    document.documentElement.dataset.startupResponsiveRafMaxGapMs =
      startupResponsiveRafMaxGapMs.toFixed(3);
    document.documentElement.dataset.startupPostReadyRafFramesObserved = String(
      startupPostReadyRafFramesObserved,
    );
    document.documentElement.dataset.startupPostReadyRafMaxGapMs =
      startupPostReadyRafMaxGapMs.toFixed(3);
    const observerTimedOut = now - startupStartedAt >= 120_000;
    const observerComplete = startupPostReadyRafFramesObserved >= 10 || observerTimedOut;
    document.documentElement.dataset.startupRafObserverComplete = String(observerComplete);
    document.documentElement.dataset.startupRafObserverTimedOut = String(observerTimedOut);
    if (!observerComplete) window.requestAnimationFrame(observeStartupRaf);
  };
  // This independent heartbeat starts before adapter/scene construction. The
  // old animation-loop-only sample missed exactly the pre-loop freeze reported
  // by users, so it could label a multi-second startup stall as healthy.
  window.requestAnimationFrame(observeStartupRaf);

  const startupStatus = document.createElement('div');
  startupStatus.id = 'startup-status';
  startupStatus.setAttribute('role', 'status');
  startupStatus.setAttribute('aria-live', 'polite');
  startupStatus.style.cssText = [
    'position:fixed',
    'left:50%',
    'top:18px',
    'transform:translateX(-50%)',
    'z-index:2147483646',
    'min-width:min(420px,calc(100vw - 32px))',
    'padding:10px 14px',
    'border:1px solid rgba(137,194,255,.7)',
    'border-radius:8px',
    'background:rgba(8,18,34,.92)',
    'color:#eaf5ff',
    'font:13px/1.45 ui-monospace,SFMono-Regular,Consolas,monospace',
    'text-align:center',
    'box-shadow:0 6px 24px rgba(0,0,0,.4)',
    'pointer-events:none',
    'transition:opacity .2s ease',
  ].join(';');
  document.body.appendChild(startupStatus);
  const setStartupStatus = (phase: string, text: string): void => {
    document.documentElement.dataset.startupPhase = phase;
    startupStatus.textContent = text;
  };
  setStartupStatus('adapter', '正在初始化 WebGPU…');
  // Guarantee that the status DOM is painted before any cold driver or scene
  // setup work starts. A long initialization must no longer look like a dead tab.
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
  });

  const stackPreset = getStackPresetFromUrl();
  const avbdProfileRequest = getAvbdProfileRequestFromUrl();
  const fixedIterationsOverride = getFixedIterationsOverrideFromUrl();
  const requestedAvbdProfile = getAvbdProfile(avbdProfileRequest.id);
  const perfEmitConfig = loadPerfEmitConfig();
  const stackConfig = STACK_PRESET_CONFIGS[stackPreset];
  const MAX_BODIES = stackConfig.maxBodies;
  // Presentation cost follows the authored population as well as the named
  // Coliseum presets. grid-64x10 also starts with 64,000 visible bodies and
  // must not accidentally inherit the 10K SMAA/dual-lobe/1.25-DPR tier.
  const isVeryLargeScene = stackPreset === 'coliseum-50k'
    || stackPreset === 'coliseum-64k'
    || stackConfig.initialCount >= 50_000;
  const initialPairManifoldSlots = stackConfig.physics.pairManifoldSlots ?? 8;
  const contactRecordCapacity = (
    stackConfig.physics.maxPersistentManifolds
      ?? MAX_BODIES * stackConfig.physics.maxPairsPerBodyBroadphase
  ) * initialPairManifoldSlots;
  const isHighCapacityScene = contactRecordCapacity >= 250_000;
  // GTAO filter quality follows the number of visible bodies, not the size of
  // a deliberately over-provisioned contact pool. Raster/MSAA budgeting stays
  // separately tied to the solver workload below.
  const isLargePresentationScene = isVeryLargeScene || stackConfig.maxBodies >= 10_000;
  // Shadow quality follows the actual visible caster population. Coliseum
  // bricks participate in the depth pass, so they use the dense-scene tier
  // instead of the former projectile-only sparse-caster exception.
  const referenceLightingQuality: ReferenceLightingQuality = isVeryLargeScene
    ? 'veryLarge'
    : isLargePresentationScene
      ? 'large'
      : 'normal';
  // Raster/MSAA cost must remain on the already-certified contact-capacity
  // tier. A 1,000-body scene can still execute a 327k-record solver graph; 4x
  // MSAA at 2x DPR would multiply its fragment budget by up to 10.24x.
  // 50K/64K spend their budget on the AVBD compute graph and storage-driven
  // instances. Native-resolution post AA is cleaner and substantially cheaper
  // than rendering every full-screen GTAO/SMAA target at 1.25x DPR.
  const maxRenderPixelRatio = isVeryLargeScene ? 1.0 : isHighCapacityScene ? 1.25 : 2.0;
  const defaultShadowsEnabled = true;
  const initialShadowsEnabled = loadShadowsEnabled(defaultShadowsEnabled);
  // GTAO is part of the default presentation for every scene. Dense 10k-64k
  // tests use the high-resolution scalable profile below, so they do not need
  // a special default-off exception.
  const defaultGtaoEnabled = true;
  const storedGtaoEnabled = loadGtaoEnabled(defaultGtaoEnabled);
  const gtaoQueryValue = new URLSearchParams(window.location.search).get('gtao')
    ?.trim()
    .toLowerCase();
  const gtaoQueryOverride = gtaoQueryValue === '1' || gtaoQueryValue === 'true'
    || gtaoQueryValue === 'on'
      ? true
      : gtaoQueryValue === '0' || gtaoQueryValue === 'false' || gtaoQueryValue === 'off'
        ? false
        : null;
  // Explicit deep links are useful for deterministic visual/performance
  // validation and do not overwrite the user's stored preference.
  const initialGtaoEnabled = gtaoQueryOverride
    ?? storedGtaoEnabled;
  document.documentElement.dataset.gtaoQueryOverride = gtaoQueryOverride === null
    ? 'none'
    : String(gtaoQueryOverride);
  const defaultGtaoIntensity = 1.0;
  const defaultGtaoRadius = 0.8;
  const initialGtaoIntensity = loadGtaoScalar(
    GTAO_INTENSITY_STORAGE_KEY,
    LEGACY_AO_INTENSITY_STORAGE_KEY,
    defaultGtaoIntensity,
    0.0,
    10.0,
  );
  const initialGtaoRadius = loadGtaoScalar(
    GTAO_RADIUS_STORAGE_KEY,
    LEGACY_AO_RADIUS_STORAGE_KEY,
    defaultGtaoRadius,
    0.1,
    10.0,
  );

  // Inspector instrumentation is intentionally absent from the production
  // physics path; the regression page can opt in explicitly when needed.
  const ctx = await initRenderer({
    enableInspector: false,
    // Dense scenes are physics stress tests. Avoid multiplying every shaded
    // instance by a 4x MSAA target (and by an unbounded HiDPI scale) while the
    // same GPU is executing the AVBD compute graph.
    antialias: !isHighCapacityScene,
    maxPixelRatio: maxRenderPixelRatio,
  });
  setStartupStatus('scene', '正在构建场景与 AVBD 数据…');
  const frameSubmitObserver = getGPUQueueSubmitObserver(ctx.device.queue);
  let presentationDirty = true;
  const lastPresentedCameraPosition = new THREE.Vector3(
    Number.NaN,
    Number.NaN,
    Number.NaN,
  );
  const lastPresentedCameraQuaternion = new THREE.Quaternion(
    Number.NaN,
    Number.NaN,
    Number.NaN,
    Number.NaN,
  );
  let renderOnDemandSkippedFrames = 0;
  let renderOnDemandUnexpectedSubmitFrames = 0;
  let presentationRenderFrames = 0;
  let lastFrameObservedSubmitCount = -1;
  let lastRenderOnDemandIdle = false;

  type PendingPresentationResize = {
    width: number;
    height: number;
    pixelRatio: number;
    serial: number;
    applyAfter: number;
  };
  let appliedPresentationWidth = Math.max(2, Math.round(window.innerWidth));
  let appliedPresentationHeight = Math.max(2, Math.round(window.innerHeight));
  let appliedPresentationPixelRatio = ctx.renderer.getPixelRatio();
  let pendingPresentationResize: PendingPresentationResize | null = null;
  let presentationResizeSerial = 0;
  let forcedPresentationFrames = 2;
  const queuePresentationResize = (): void => {
    const width = Math.round(window.innerWidth);
    const height = Math.round(window.innerHeight);
    // Browsers can report a transient 0/1px viewport while minimizing or
    // moving a window between displays. Keep the last valid backing store;
    // stretching it is preferable to allocating and presenting a 2x2 canvas.
    if (width < 16 || height < 16) {
      document.documentElement.dataset.presentationResizeState = 'ignored-transient';
      return;
    }
    const pixelRatio = Math.min(
      Math.max(0.5, Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : 1),
      maxRenderPixelRatio,
    );
    const queued = pendingPresentationResize;
    if (
      (queued?.width ?? appliedPresentationWidth) === width
      && (queued?.height ?? appliedPresentationHeight) === height
      && Math.abs((queued?.pixelRatio ?? appliedPresentationPixelRatio) - pixelRatio) <= 1e-6
    ) {
      return;
    }
    pendingPresentationResize = {
      width,
      height,
      pixelRatio,
      serial: ++presentationResizeSerial,
      // Coalesce resize storms so a 4K GTAO MRT is rebuilt only after the user
      // pauses dragging. CSS continues to display the last valid frame.
      applyAfter: performance.now() + 75,
    };
    presentationDirty = true;
    document.documentElement.dataset.presentationResizeState = 'queued';
    document.documentElement.dataset.presentationResizeSerial = String(presentationResizeSerial);
  };
  window.addEventListener('resize', queuePresentationResize);
  let shuttingDown = false;
  window.addEventListener('error', (event) => {
    document.documentElement.dataset.runtimeError = event.message || 'unknown window error';
    if (event.error instanceof Error && event.error.stack) {
      document.documentElement.dataset.runtimeErrorStack = event.error.stack;
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason.message : String(event.reason);
    document.documentElement.dataset.runtimeUnhandledRejection = reason;
  });
  let gpuRecoveryScheduled = false;
  let gpuValidationSubmissionHalted = false;
  let gpuValidationHaltReason = '';
  let applyGpuValidationSimulationHalt: (() => void) | null = null;
  const GPU_RECOVERY_HISTORY_KEY = 'pmavbd.gpuRecoveryHistory.v1';
  const GPU_RECOVERY_WINDOW_MS = 60_000;
  const GPU_RECOVERY_MAX_RELOADS = 2;

  const reserveGpuRecoveryReload = (): boolean => {
    const now = Date.now();
    try {
      const stored = window.sessionStorage.getItem(GPU_RECOVERY_HISTORY_KEY);
      let parsed: unknown = [];
      if (stored !== null) {
        try {
          parsed = JSON.parse(stored);
        } catch {
          // A stale/malformed value must not permanently disable recovery.
          parsed = [];
        }
      }
      const recent = Array.isArray(parsed)
        ? parsed
          .filter((entry): entry is number => Number.isFinite(entry))
          .filter((entry) => now - entry < GPU_RECOVERY_WINDOW_MS)
        : [];
      document.documentElement.dataset.gpuRecoveryReloadCount = String(recent.length);
      if (recent.length >= GPU_RECOVERY_MAX_RELOADS) return false;
      recent.push(now);
      window.sessionStorage.setItem(GPU_RECOVERY_HISTORY_KEY, JSON.stringify(recent));
      document.documentElement.dataset.gpuRecoveryReloadCount = String(recent.length);
      return true;
    } catch (error) {
      // If storage is unavailable there is no durable way to prevent a reload
      // loop.  Fail closed and leave a visible, inspectable error instead.
      console.warn('[WebGPU] recovery rate-limit storage unavailable:', error);
      return false;
    }
  };

  const showGpuRecoveryNotice = (reason: string, willReload: boolean): void => {
    const notice = document.createElement('div');
    notice.id = 'webgpu-recovery-notice';
    notice.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:16px',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'max-width:min(760px,calc(100vw - 32px))',
      'padding:10px 14px',
      'border:1px solid rgba(255,120,120,.9)',
      'border-radius:8px',
      'background:rgba(64,8,8,.94)',
      'color:#fff',
      'font:13px/1.45 ui-monospace,SFMono-Regular,Consolas,monospace',
      'box-shadow:0 6px 24px rgba(0,0,0,.45)',
      'pointer-events:none',
    ].join(';');
    notice.textContent = willReload
      ? `WebGPU queue stopped responding (${reason}). Reloading with a fresh device...`
      : `WebGPU queue stopped responding (${reason}). Automatic recovery was rate-limited; reload the page manually.`;
    document.body.appendChild(notice);
  };

  const showGpuValidationHaltNotice = (reason: string): void => {
    if (document.getElementById('webgpu-validation-halt-notice')) return;
    const notice = document.createElement('div');
    notice.id = 'webgpu-validation-halt-notice';
    notice.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:16px',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'max-width:min(760px,calc(100vw - 32px))',
      'padding:10px 14px',
      'border:1px solid rgba(255,182,72,.95)',
      'border-radius:8px',
      'background:rgba(70,38,4,.96)',
      'color:#fff',
      'font:13px/1.45 ui-monospace,SFMono-Regular,Consolas,monospace',
      'box-shadow:0 6px 24px rgba(0,0,0,.45)',
      'pointer-events:none',
    ].join(';');
    notice.textContent = `WebGPU validation failed; physics submissions are halted (${reason}). Reload after fixing or changing the scene.`;
    document.body.appendChild(notice);
  };

  const scheduleGpuRecovery = (reason: string): void => {
    if (shuttingDown || gpuRecoveryScheduled) return;
    gpuRecoveryScheduled = true;
    document.documentElement.dataset.gpuQueueState = 'recovering';
    document.documentElement.dataset.gpuRecoveryReason = reason;
    document.documentElement.dataset.gpuRecoveryRateLimited = 'false';

    // A rejected or timed-out fence is not completion. Stop the producer and
    // replace the whole WebGPU device; never decrement the in-flight count and
    // continue submitting work against a queue whose progress is unknown.
    try {
      ctx.renderer.setAnimationLoop(null);
    } catch (error) {
      // Continue to device replacement even if the renderer is already lost.
      console.warn('[WebGPU] failed to stop renderer loop during recovery:', error);
    }
    const willReload = reserveGpuRecoveryReload();
    if (!willReload) {
      document.documentElement.dataset.gpuQueueState = 'failed';
      document.documentElement.dataset.gpuRecoveryRateLimited = 'true';
    }
    showGpuRecoveryNotice(reason, willReload);
    console.error(`[WebGPU] queue recovery requested: ${reason}`);

    if (willReload) {
      window.setTimeout(() => {
        if (!shuttingDown) window.location.reload();
      }, 250);
    }
  };

  ctx.device.addEventListener('uncapturederror', (event) => {
    const message = typeof event.error?.message === 'string'
      ? event.error.message
      : event.error instanceof Error
        ? event.error.message
        : String(event.error);
    const errorKind = event.error?.constructor?.name ?? 'GPUError';
    document.documentElement.dataset.webgpuUncapturedError = message;
    document.documentElement.dataset.webgpuUncapturedErrorKind = errorKind;
    console.error('[WebGPU] uncaptured error:', event.error);
    // Internal/OOM errors can leave command execution unusable while queue
    // fences still resolve and device.lost has not fired yet. Route them into
    // the same bounded full-device recovery instead of reporting "healthy" on
    // a simulation that can no longer make progress. Validation errors remain
    // a visible, non-reloading correctness halt: retrying the same invalid
    // command graph every animation frame can corrupt telemetry and lock up
    // the page, while deterministic automatic reloads would only loop.
    if (errorKind === 'GPUOutOfMemoryError' || errorKind === 'GPUInternalError') {
      scheduleGpuRecovery(`uncaptured-${errorKind}:${message}`);
    } else if (errorKind === 'GPUValidationError' && !gpuValidationSubmissionHalted) {
      gpuValidationSubmissionHalted = true;
      gpuValidationHaltReason = message;
      document.documentElement.dataset.webgpuValidationHalted = 'true';
      document.documentElement.dataset.webgpuValidationHaltReason = message;
      document.documentElement.dataset.physicsSubmissionHalted = 'true';
      document.documentElement.dataset.gpuQueueState = 'validation-halted';
      showGpuValidationHaltNotice(message);
      applyGpuValidationSimulationHalt?.();
    }
  });

  document.documentElement.dataset.webgpuDeviceStatus = 'ok';
  document.documentElement.dataset.webgpuValidationHalted = 'false';
  document.documentElement.dataset.physicsSubmissionHalted = 'false';
  document.documentElement.dataset.gpuQueueState = 'healthy';
  document.documentElement.dataset.gpuRecoveryRateLimited = 'false';
  void ctx.device.lost.then((info) => {
    document.documentElement.dataset.webgpuDeviceStatus = 'lost';
    document.documentElement.dataset.webgpuDeviceLostReason = info.reason;
    if (!shuttingDown) {
      console.error(`[WebGPU] device lost (${info.reason}): ${info.message}`);
      scheduleGpuRecovery(`device-lost:${info.reason}`);
    }
  }).catch((error: unknown) => {
    if (!shuttingDown) scheduleGpuRecovery(`device-lost-promise:${String(error)}`);
  });
  ctx.controls.enabled = false;
  ctx.camera.position.set(
    stackConfig.cameraPosition[0],
    stackConfig.cameraPosition[1],
    stackConfig.cameraPosition[2],
  );
  ctx.camera.lookAt(
    stackConfig.cameraLookAt[0],
    stackConfig.cameraLookAt[1],
    stackConfig.cameraLookAt[2],
  );

  // GPU-driven instances deliberately have no CPU-side world matrices to scan
  // for bounds. Fit the reference light once from the authored presentation
  // volume instead of reading poses back or including the oversized floor.
  const lightingCenter = new THREE.Vector3(...stackConfig.cameraLookAt);
  const authoredCameraPosition = new THREE.Vector3(...stackConfig.cameraPosition);
  const cameraDistance = authoredCameraPosition.distanceTo(lightingCenter);
  const lightingHorizontalRadius = isVeryLargeScene
    ? 31
    : THREE.MathUtils.clamp(cameraDistance * 0.78 + 2, 6, 80);
  const lightingBounds = isVeryLargeScene
    ? new THREE.Box3(
        new THREE.Vector3(-31, -1, -31),
        new THREE.Vector3(31, 19, 31),
      )
    : new THREE.Box3(
        new THREE.Vector3(
          lightingCenter.x - lightingHorizontalRadius,
          Math.min(-1, lightingCenter.y - lightingHorizontalRadius * 0.65),
          lightingCenter.z - lightingHorizontalRadius,
        ),
        new THREE.Vector3(
          lightingCenter.x + lightingHorizontalRadius,
          lightingCenter.y + Math.max(6, lightingHorizontalRadius * 0.72),
          lightingCenter.z + lightingHorizontalRadius,
        ),
      );
  const areaLightEnabled = !isHighCapacityScene;
  if (areaLightEnabled) await prepareReferenceAreaLightLtc();
  const referenceLighting = createReferenceLighting({
    renderer: ctx.renderer,
    scene: ctx.scene,
    quality: referenceLightingQuality,
    bounds: lightingBounds,
    enabled: true,
    shadowsEnabled: initialShadowsEnabled,
    // The LTC rectangular key remains active for presentation scenes whose
    // contact/instance budget leaves fragment headroom. High-capacity stress
    // scenes use the same warm key direction plus environment fill, without
    // paying two LTC lookups on every PBR receiver pixel. This is the main 1K
    // and 64K safeguard discovered by the same-machine render comparison.
    areaLightEnabled,
  });
  let shadowSceneDirty = true;
  let shadowUpdateCount = 0;
  let lastShadowSimulationStepCount = -1;
  let lastShadowBodyCount = -1;
  let lastShadowPresentedPoseRevision = -1;
  let lastPresentationTimestamp = 0;
  let lastPresentationTelemetryTimestamp = 0;
  let presentationFpsEma = 60;
  document.documentElement.dataset.lightingImplementation =
    'webgpu-reference-rect-area-tight-scheduled-directional-v1';
  document.documentElement.dataset.lightingQuality = referenceLightingQuality;
  document.documentElement.dataset.lightingAreaLightEnabled = String(areaLightEnabled);
  document.documentElement.dataset.lightingZeroReadback = 'true';
  document.documentElement.dataset.shadowMapSize = String(
    referenceLighting.profile.shadowMapSize,
  );
  document.documentElement.dataset.shadowFilter = referenceLighting.profile.shadowFilter;
  document.documentElement.dataset.shadowMaxUpdatesPerSecond = String(
    referenceLighting.profile.maxShadowUpdatesPerSecond,
  );
  document.documentElement.dataset.shadowUpdatePolicy = 'physics-pose-synchronous';
  document.documentElement.dataset.shadowCasterTier = referenceLightingQuality;
  document.documentElement.dataset.shadowUpdateCount = '0';

  const setShadowsEnabled = (enabled: boolean): void => {
    presentationDirty = true;
    shadowSceneDirty = true;
    referenceLighting.setShadowsEnabled(enabled);
    document.documentElement.dataset.shadowsEnabled = String(enabled);
    ctx.scene.traverse((object) => {
      const material = (object as THREE.Mesh).material;
      if (!material) return;
      if (Array.isArray(material)) {
        for (const entry of material) entry.needsUpdate = true;
      } else {
        material.needsUpdate = true;
      }
    });
  };
  setShadowsEnabled(initialShadowsEnabled);

  // Physics
  const physics = new PhysicsEngine(ctx.device, {
    maxBodies: MAX_BODIES,
    gravity: [0, -9.81, 0],
    substeps: stackConfig.physics.substeps ?? 4,
    deltaTime: 1 / 60,
    pairManifoldSlots: initialPairManifoldSlots,
    solverIterations: 4,
    avbdAdaptiveIterations: stackConfig.physics.avbdAdaptiveIterations,
    avbdAdaptiveMinIterations: stackConfig.physics.avbdAdaptiveMinIterations,
    avbdAdaptiveDualTolerance: stackConfig.physics.avbdAdaptiveDualTolerance,
    avbdAdaptivePenaltyTolerance: stackConfig.physics.avbdAdaptivePenaltyTolerance,
    avbdCacheContactTangentInvLength:
      stackConfig.physics.avbdCacheContactTangentInvLength,
    avbdDenseContactSolverState: stackConfig.physics.avbdDenseContactSolverState,
    avbdSpecializeReferenceFinalizer:
      stackConfig.physics.avbdSpecializeReferenceFinalizer,
    avbdCaptureBodyDeltaCache: stackConfig.physics.avbdCaptureBodyDeltaCache,
    avbdPackedContactSolverGeometry:
      stackConfig.physics.avbdPackedContactSolverGeometry,
    avbdFuseFinalCaptureCommit:
      stackConfig.physics.avbdFuseFinalCaptureCommit,
    avbdManifoldCapture: stackConfig.physics.avbdManifoldCapture,
    avbdCooperativeBodySolve: stackConfig.physics.avbdCooperativeBodySolve,
    pairSolveColorCount: stackConfig.physics.maxPairSolveColorCount,
    maxPairsPerBodyBroadphase: stackConfig.physics.maxPairsPerBodyBroadphase,
    maxPersistentManifolds: stackConfig.physics.maxPersistentManifolds,
    contactPoolMode: stackConfig.physics.contactPoolMode,
    compactContactPoolCapacity: stackConfig.physics.compactContactPoolCapacity,
    maxContactsPerBodySolver: stackConfig.physics.maxContactsPerBodySolver ?? 64,
    maxJoints: stackConfig.physics.maxJoints
      ?? (JOINT_PRESETS.has(stackPreset) ? undefined : 0),
    maxSprings: stackConfig.physics.maxSprings
      ?? (SPRING_PRESETS.has(stackPreset) ? undefined : 0),
    maxIgnoredPairs: stackConfig.physics.maxIgnoredPairs,
    enableSleeping: stackConfig.physics.enableSleeping,
    enableSleepingSparseBroadphase:
      stackConfig.physics.enableSleepingSparseBroadphase,
    sleepingSparseMinimumBodyCount:
      stackConfig.physics.sleepingSparseMinimumBodyCount,
    sleepFrames: stackConfig.physics.sleepFrames,
    sleepLinearSpeed: stackConfig.physics.sleepLinearSpeed,
    sleepAngularSpeed: stackConfig.physics.sleepAngularSpeed,
    enableBroadphasePairDedup: stackConfig.physics.enableBroadphasePairDedup,
    // Keep the interactive demo out of catch-up spirals: if physics is slower
    // than real time, advance one fixed step per render frame and let sim time
    // slow down instead of stacking multiple full GPU solves into one frame.
    maxFixedStepsPerFrame: 1,
    enableBvhBuild: true,
    bvhBuildOnce: false,
    // Refit bounds every step; re-sort Morton topology periodically instead
    // of rebuilding the full LBVH every frame.
    bvhRebuildIntervalFrames: isHighCapacityScene ? 30 : 12,
    bvhWaitForGpuCompletion: false,
    avbdFriction: AVBD_FRICTION_STATIC,
    avbdBodySolveMode: 'colored',
  });
  const initialFriction = stackPreset === 'waterfall-gutter'
    ? WATERFALL_GUTTER_PRODUCTION_MANIFEST.friction
    : AVBD_FRICTION_STATIC;
  const logAvbdConfig = (reason: string): void => {
    const exactProfileParameters = physics.getSubsteps() === requestedAvbdProfile.substeps
      && physics.getSolverIterations() === requestedAvbdProfile.solverIterations
      && physics.getAvbdDualUpdateBeta() === requestedAvbdProfile.linearDualUpdateBeta
      && physics.getAvbdAngularDualUpdateBeta() === requestedAvbdProfile.angularDualUpdateBeta
      && physics.getAvbdRegularizationAlpha() === requestedAvbdProfile.regularizationAlpha
      && physics.getAvbdPenaltyDecayGamma() === requestedAvbdProfile.penaltyDecayGamma
      && physics.getAvbdPairSweeps() === requestedAvbdProfile.pairSweeps
      && physics.getAvbdPenaltyFloor() === requestedAvbdProfile.penaltyFloor
      && (
        !requestedAvbdProfile.executionPolicy
        || physics.getAvbdAdaptiveIterationsConfigured()
          === requestedAvbdProfile.executionPolicy.adaptiveIterations
      );
    console.info(
      `[AVBD Config] reason=${reason} profile=${requestedAvbdProfile.id} ` +
      `parameterExact=${exactProfileParameters ? 1 : 0} ` +
      `iterationPolicyConfigured=${physics.getAvbdAdaptiveIterationsConfigured()
        ? `adaptive-min-${physics.getAvbdAdaptiveMinIterations()}`
        : 'fixed'} ` +
      `schedule=${requestedAvbdProfile.executionPolicy?.schedule
        ?? 'scene-production-policy'} ` +
      `serialBodyOrderParity=${requestedAvbdProfile.executionPolicy?.serialBodyOrderParity
        ? 1
        : 0} ` +
      `mode=colored-cached-taylor-world-inertia ` +
      `substeps=${physics.getSubsteps()} solverIterations=${physics.getSolverIterations()} ` +
      `manifoldSlots=${physics.getPairManifoldSlots()} ` +
      `pairSweeps=${physics.getAvbdPairSweeps()} ` +
      `penetratingSupportFloor=${physics.getAvbdPreventPenetratingNormalDropout() ? 1 : 0} ` +
      `betaLin=${physics.getAvbdDualUpdateBeta().toFixed(3)} ` +
      `betaAng=${physics.getAvbdAngularDualUpdateBeta().toFixed(3)} ` +
      `alpha=${physics.getAvbdRegularizationAlpha().toFixed(3)} ` +
      `gamma=${physics.getAvbdPenaltyDecayGamma().toFixed(5)} ` +
      `kStart=${physics.getAvbdPenaltyFloor().toFixed(3)} ` +
      `margin=${physics.getCollisionMargin().toFixed(4)} ` +
      `restitution=${physics.getContactRestitution().toFixed(3)} ` +
      `restitutionIterations=${physics.getRestitutionIterations()}`,
    );
  };
  const initialSimulationPaused = loadSimulationPaused(false);
  const initialLogEveryStep = isVeryLargeScene
    ? false
    : loadLogEveryStep(false);
  applyAvbdProfile(physics, requestedAvbdProfile);
  if (
    !avbdProfileRequest.explicit
    && stackConfig.physics.avbdPenaltyDecayGamma !== undefined
  ) {
    // Keep the opt-in official-exact profile untouched. The default Coliseum
    // route instead uses its separately certified production-robust decay.
    physics.setAvbdPenaltyDecayGamma(stackConfig.physics.avbdPenaltyDecayGamma);
  }
  if (stackConfig.physics.substeps !== undefined) {
    // Scene-certified temporal resolution overrides only the timestep split;
    // all AVBD primal/dual parameters still come from the selected profile.
    physics.setSubsteps(stackConfig.physics.substeps);
  }
  if (fixedIterationsOverride === true) {
    // A/B surface for a precision-increasing optimization: keep the selected
    // profile's equations and full iteration count, but remove residual-driven
    // early-out and its dispatch/atomic bookkeeping. This never lowers the
    // number of nonlinear sweeps.
    physics.setAvbdAdaptiveIterationsConfigured(false);
  } else if (
    fixedIterationsOverride === false
    && !requestedAvbdProfile.executionPolicy
  ) {
    // Explicit control arm. Parameter-only profiles retain the scene-certified
    // adaptive policy exactly; execution-policy profiles such as official-exact
    // remain authoritative and cannot be weakened by this query.
    physics.setAvbdAdaptiveIterationsConfigured(
      stackConfig.physics.avbdAdaptiveIterations ?? false,
    );
  }
  document.documentElement.dataset.avbdFixedIterationsOverride =
    fixedIterationsOverride === null ? 'none' : String(fixedIterationsOverride);
  if (stackPreset === 'newtons-cradles-demo') {
    // The paper/reference AVBD contact is intentionally inelastic. This scene
    // opts into a clearly separated velocity-level elastic-impact extension,
    // while the AVBD primal/dual position solve remains unchanged.
    physics.setSubsteps(4);
    physics.setCollisionMargin(0.0);
    physics.setContactRestitution(1.0, 8, 0.1);
  }
  if (stackConfig.physics.activePairSolveColorCount !== undefined) {
    physics.setPairSolveColorCount(stackConfig.physics.activePairSolveColorCount);
  }
  const defaultDualUpdateBeta = physics.getAvbdDualUpdateBeta();
  const defaultAngularDualUpdateBeta = physics.getAvbdAngularDualUpdateBeta();
  const defaultRegularizationAlpha = physics.getAvbdRegularizationAlpha();
  const defaultPenaltyDecayGamma = physics.getAvbdPenaltyDecayGamma();
  const defaultAvbdPenaltyFloor = physics.getAvbdPenaltyFloor();
  const defaultSolverIterations = requestedAvbdProfile.executionPolicy
    ? requestedAvbdProfile.solverIterations
    : stackConfig.physics.solverIterations ?? physics.getSolverIterations();
  // Large-scene performance and stability must not depend on tuning values
  // persisted by an unrelated small-scene experiment. Users can still change
  // these controls explicitly after the scene has loaded.
  // Certified routes always start from their declared profile. Historical
  // localStorage tuning is deliberately opt-in so an old experiment cannot
  // silently turn a production-robust or reference run into a custom profile.
  const loadStoredAvbdTuning = getStoredAvbdTuningOptInFromUrl()
    && !isVeryLargeScene
    && !avbdProfileRequest.explicit;
  const initialDualUpdateBeta = !loadStoredAvbdTuning
    ? defaultDualUpdateBeta
    : loadDualUpdateBeta(defaultDualUpdateBeta);
  const initialAngularDualUpdateBeta = loadStoredAvbdTuning
    ? loadAngularDualUpdateBeta(defaultAngularDualUpdateBeta)
    : defaultAngularDualUpdateBeta;
  const initialRegularizationAlpha = !loadStoredAvbdTuning
    ? defaultRegularizationAlpha
    : loadRegularizationAlpha(defaultRegularizationAlpha);
  const initialPenaltyDecayGamma = !loadStoredAvbdTuning
    ? defaultPenaltyDecayGamma
    : loadPenaltyDecayGamma(defaultPenaltyDecayGamma);
  const initialAvbdPenaltyFloor = !loadStoredAvbdTuning
    ? defaultAvbdPenaltyFloor
    : loadAvbdPenaltyFloor(defaultAvbdPenaltyFloor);
  const initialSolverIterations = defaultSolverIterations;
  physics.setAvbdFriction(initialFriction);
  physics.setAvbdDualUpdateBetas(initialDualUpdateBeta, initialAngularDualUpdateBeta);
  physics.setAvbdRegularizationAlpha(initialRegularizationAlpha);
  physics.setAvbdPenaltyDecayGamma(initialPenaltyDecayGamma);
  physics.setAvbdPenaltyFloor(initialAvbdPenaltyFloor);
  physics.setSolverIterations(initialSolverIterations);
  logAvbdConfig('startup');
  physics.setDebugLogEveryFrame(initialLogEveryStep);

  const playerEyeOffset = 0.75;
  const floorHalfExtents: [number, number, number] = [100.0, 0.5, 100.0];
  type CollisionFilterOptions = {
    collisionGroup?: number;
    collisionMask?: number;
  };

  const buildSceneRuntime = (): SceneRuntime => {
    const sceneRoot = new THREE.Group();
    ctx.scene.add(sceneRoot);

    const staticVisuals: THREE.Mesh[] = [];
    const trackedVisualSets: Array<ReturnType<typeof createTrackedBodyVisualSet>> = [];
    const trackedSpringVisualSets: Array<ReturnType<typeof createTrackedSpringVisualSet>> = [];
    const syncedSceneVisuals: SyncedSceneVisual[] = [];
    const sceneUpdateCallbacks: Array<(dt: number) => void> = [];
    let bridgeMiniDiagnostics: MiniBridgeDiagnosticsConfig | null = null;
    let carouselDiagnostics: CarouselDiagnosticsConfig | null = null;
    let shootProjectileOverride: ((config: ProjectileShootConfig) => boolean) | null = null;
    let sceneStatusText: (() => string | null) | null = null;

    const boxCapacity = Math.max(
      1,
      Math.min(stackConfig.spawnerCapacity ?? (MAX_BODIES - 1), MAX_BODIES - 1),
    );
    const spawner = createBoxSpawner(sceneRoot, physics, {
      count: boxCapacity,
      initialCount: Math.min(stackConfig.initialCount, boxCapacity),
      halfExtents: [0.3, 0.3, 0.3],
      layout: stackConfig.layout,
      ...(stackConfig.grid ? { grid: stackConfig.grid } : {}),
      ...(stackConfig.pyramidWall ? { pyramidWall: stackConfig.pyramidWall } : {}),
      emitter: {
        enabled: false,
        rate: 24.0,
      },
      randomRotation: false,
      rotationJitter: 0.04,
    });
    const defaultEmitter = spawner.getEmitter();

    const addStaticBoxWithVisual = (
      position: [number, number, number],
      halfExtents: [number, number, number],
      options?: {
        quaternion?: [number, number, number, number];
        color?: number;
      },
    ): void => {
      physics.addBody({
        position,
        mass: 0.0,
        halfExtents,
        ...(options?.quaternion ? { quaternion: options.quaternion } : {}),
        lockRotation: true,
      });

      const visual = new THREE.Mesh(
        new THREE.BoxGeometry(halfExtents[0] * 2, halfExtents[1] * 2, halfExtents[2] * 2),
        new THREE.MeshStandardMaterial({
          color: options?.color ?? 0x586474,
          roughness: 0.85,
          metalness: 0.05,
        }),
      );
      visual.position.set(position[0], position[1], position[2]);
      if (options?.quaternion) {
        visual.quaternion.set(
          options.quaternion[0],
          options.quaternion[1],
          options.quaternion[2],
          options.quaternion[3],
        );
      }
      visual.castShadow = true;
      visual.receiveShadow = true;
      sceneRoot.add(visual);
      staticVisuals.push(visual);
    };

    const addStaticCylinderWithVisual = (
      position: [number, number, number],
      radius: number,
      halfHeight: number,
      options?: {
        color?: number;
      },
    ): void => {
      physics.addBody({
        position,
        mass: 0.0,
        halfExtents: [radius, halfHeight, radius],
        lockRotation: true,
      });

      const visual = new THREE.Mesh(
        new THREE.CylinderGeometry(radius, radius, halfHeight * 2, 28),
        new THREE.MeshStandardMaterial({
          color: options?.color ?? 0x586474,
          roughness: 0.85,
          metalness: 0.05,
        }),
      );
      visual.position.set(position[0], position[1], position[2]);
      visual.castShadow = true;
      visual.receiveShadow = true;
      sceneRoot.add(visual);
      staticVisuals.push(visual);
    };

    const addDynamicBoxWithTrackedVisual = (
      position: [number, number, number],
      halfExtents: [number, number, number],
      trackedVisuals: ReturnType<typeof createTrackedBodyVisualSet>,
      options?: {
        mass?: number;
        friction?: number;
        quaternion?: [number, number, number, number];
        linearVelocity?: [number, number, number];
        collisionGroup?: number;
        collisionMask?: number;
      },
    ): number => {
      const body = physics.addBody({
        position,
        mass: options?.mass ?? 1.0,
        halfExtents,
        friction: options?.friction ?? initialFriction,
        quaternion: options?.quaternion,
        linearVelocity: options?.linearVelocity,
        collisionGroup: options?.collisionGroup,
        collisionMask: options?.collisionMask,
      });
      trackedVisuals.addBody(body);
      return body;
    };

    const addDynamicSphereWithTrackedVisual = (
      position: [number, number, number],
      radius: number,
      trackedVisuals: ReturnType<typeof createTrackedBodyVisualSet>,
      options?: {
        mass?: number;
        friction?: number;
        quaternion?: [number, number, number, number];
        linearVelocity?: [number, number, number];
        collisionGroup?: number;
        collisionMask?: number;
      },
    ): number => {
      const body = physics.addBody({
        position,
        shapeType: 'sphere',
        radius,
        mass: options?.mass ?? 1.0,
        friction: options?.friction ?? initialFriction,
        quaternion: options?.quaternion,
        linearVelocity: options?.linearVelocity,
        collisionGroup: options?.collisionGroup,
        collisionMask: options?.collisionMask,
      });
      trackedVisuals.addBody(body);
      return body;
    };

    const isBridgePreset =
      stackPreset === 'bridge-demo'
      || stackPreset === 'bridge-demo-empty'
      || stackPreset === 'bridge-demo-fixed';
    const isSpringPreset =
      stackPreset === 'spring-demo'
      || stackPreset === 'spring-ratio';
    const isSpherePreset = stackPreset === 'sphere-demo';

    if (
      stackPreset !== 'waterfall-gutter'
      && stackPreset !== 'dominoes-advanced-demo'
      && stackPreset !== 'bunny-soft-body'
      && !isBridgePreset
      && !isSpringPreset
    ) {
      addStaticBoxWithVisual(
        [0.0, -floorHalfExtents[1], 0.0],
        floorHalfExtents,
        { color: 0x495563 },
      );
    }

    if (stackPreset === 'waterfall-gutter') {
      buildWaterfallGutterDemo({
        addStaticBox: (position, halfExtents, boxOptions) => addStaticBoxWithVisual(
          position,
          halfExtents,
          boxOptions,
        ),
      });
    }

    if (isBridgePreset) {
      const COLLISION_GROUP_WORLD = 0x01;
      const COLLISION_GROUP_PLANK = 0x02;
      const COLLISION_GROUP_LOAD = 0x04;
      const rigidJointStiffness = Number.POSITIVE_INFINITY;
      const useFixedBridgeJoints = stackPreset === 'bridge-demo-fixed';
      const spawnBridgeLoads = stackPreset === 'bridge-demo';
      const plankCount = 40;
      const plankHalfExtents: [number, number, number] = [0.5, 0.25, 2.0];
      const plankHalfLength = plankHalfExtents[0];
      const plankHalfWidth = plankHalfExtents[2];
      const bridgeY = 10.0;
      const plankDensity = 1.0;
      const loadHalfExtents: [number, number, number] = [0.5, 0.5, 0.5];
      const boxMassFromDensity = (halfExtents: [number, number, number], density: number): number => {
        const volume = halfExtents[0] * 2.0 * halfExtents[1] * 2.0 * halfExtents[2] * 2.0;
        return volume * density;
      };
      const plankMass = boxMassFromDensity(plankHalfExtents, plankDensity);
      const loadMass = boxMassFromDensity(loadHalfExtents, 1.0);
      const worldFilter: CollisionFilterOptions = {
        collisionGroup: COLLISION_GROUP_WORLD,
        collisionMask: COLLISION_GROUP_PLANK | COLLISION_GROUP_LOAD,
      };
      const plankFilter: CollisionFilterOptions = {
        collisionGroup: COLLISION_GROUP_PLANK,
        collisionMask: COLLISION_GROUP_WORLD | COLLISION_GROUP_PLANK | COLLISION_GROUP_LOAD,
      };
      const loadFilter: CollisionFilterOptions = {
        collisionGroup: COLLISION_GROUP_LOAD,
        collisionMask: COLLISION_GROUP_WORLD | COLLISION_GROUP_PLANK | COLLISION_GROUP_LOAD,
      };

      addStaticBoxWithVisual(
        [0.0, -floorHalfExtents[1], 0.0],
        floorHalfExtents,
        { color: 0x495563 },
      );
      physics.setBodyCollisionFilter(0, worldFilter.collisionGroup ?? 0x01, worldFilter.collisionMask ?? 0xff);

      const loadCols = Math.max(1, Math.floor(plankCount / 4));
      const loadRows = Math.max(1, Math.floor(plankCount / 8));
      const plankVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
        capacity: plankCount,
        halfExtents: plankHalfExtents,
        color: 0x8f6737,
        roughness: 0.82,
        metalness: 0.04,
      });
      trackedVisualSets.push(plankVisuals);
      const loadVisuals = spawnBridgeLoads ? createTrackedBodyVisualSet(sceneRoot, physics, {
        capacity: loadCols * loadRows,
        halfExtents: loadHalfExtents,
        color: 0xb24c3f,
        roughness: 0.7,
        metalness: 0.08,
      }) : null;
      if (loadVisuals) {
        trackedVisualSets.push(loadVisuals);
      }

      const plankIds: number[] = [];
      for (let i = 0; i < plankCount; i++) {
        const mass = (i === 0 || i === plankCount - 1) ? 0.0 : plankMass;
        plankIds.push(addDynamicBoxWithTrackedVisual(
          [i - plankCount * 0.5, bridgeY, 0.0],
          plankHalfExtents,
          plankVisuals,
          {
            mass,
            friction: 0.5,
            collisionGroup: plankFilter.collisionGroup,
            collisionMask: plankFilter.collisionMask,
          },
        ));
      }
      for (let i = 1; i < plankCount; i++) {
        physics.setBodyPairCollisionIgnored(plankIds[i - 1]!, plankIds[i]!, true);
        if (useFixedBridgeJoints) {
          const joint = physics.addFixedJoint(
            plankIds[i - 1]!,
            plankIds[i]!,
            [plankHalfLength, 0.0, 0.0],
            [-plankHalfLength, 0.0, 0.0],
            rigidJointStiffness,
            true,
          );
        } else {
          physics.addSphericalJoint(
            plankIds[i - 1]!,
            plankIds[i]!,
            [plankHalfLength, 0.0, plankHalfWidth],
            [-plankHalfLength, 0.0, plankHalfWidth],
            rigidJointStiffness,
            true,
          );
          physics.addSphericalJoint(
            plankIds[i - 1]!,
            plankIds[i]!,
            [plankHalfLength, 0.0, -plankHalfWidth],
            [-plankHalfLength, 0.0, -plankHalfWidth],
            rigidJointStiffness,
            true,
          );
        }
      }
      if (loadVisuals) {
        for (let x = 0; x < loadCols; x++) {
          for (let y = 0; y < loadRows; y++) {
            addDynamicBoxWithTrackedVisual(
              [x - plankCount / 8.0, y + 12.0, 0.0],
              loadHalfExtents,
              loadVisuals,
              {
                mass: loadMass,
                friction: 0.5,
                collisionGroup: loadFilter.collisionGroup,
                collisionMask: loadFilter.collisionMask,
              },
            );
          }
        }
      }
    }

    if (isSpringPreset) {
      const springSceneHeightOffset = 9.5;
      const springGroundHalfExtents: [number, number, number] = [100.0, 0.5, 100.0];
      addStaticBoxWithVisual(
        [0.0, -springGroundHalfExtents[1], 0.0],
        springGroundHalfExtents,
        { color: 0x495563 },
      );

      if (stackPreset === 'spring-demo') {
        const springGridCols = 10;
        const springGridRows = 10;
        const springCellSpacingX = 5.0;
        const springCellSpacingZ = 5.0;
        const springAnchorY = 14.0 + springSceneHeightOffset;
        const springBlockY = 8.0 + springSceneHeightOffset;
        const springCount = springGridCols * springGridRows;
        const springSeed01 = (index: number, salt: number): number => {
          const value = Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453123;
          return value - Math.floor(value);
        };
        const springLines = createTrackedSpringVisualSet(sceneRoot, {
          capacity: springCount,
          color: 0x9fd0ff,
        });
        trackedSpringVisualSets.push(springLines);
        const anchorVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
          capacity: springCount,
          halfExtents: [0.5, 0.5, 0.5],
          color: 0x7f5d36,
          roughness: 0.82,
          metalness: 0.04,
        });
        const blockVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
          capacity: springCount,
          halfExtents: [1.0, 1.0, 1.0],
          color: 0x7f5d36,
          roughness: 0.82,
          metalness: 0.04,
        });
        trackedVisualSets.push(anchorVisuals, blockVisuals);

        for (let row = 0; row < springGridRows; row++) {
          for (let col = 0; col < springGridCols; col++) {
            const springIndex = row * springGridCols + col;
            const baseX = (col - (springGridCols - 1) * 0.5) * springCellSpacingX;
            const baseZ = (row - (springGridRows - 1) * 0.5) * springCellSpacingZ;
            const blockMass = THREE.MathUtils.lerp(0.6, 2.4, springSeed01(springIndex, 0.19));
            const springStiffness = THREE.MathUtils.lerp(55.0, 220.0, springSeed01(springIndex, 0.47));
            const lateralOffset = THREE.MathUtils.lerp(-1.1, 1.1, springSeed01(springIndex, 0.83));

            const anchor = addDynamicBoxWithTrackedVisual(
              [baseX, springAnchorY, baseZ],
              [0.5, 0.5, 0.5],
              anchorVisuals,
              { mass: 0.0, friction: 0.5 },
            );
            const block = addDynamicBoxWithTrackedVisual(
              [baseX + lateralOffset, springBlockY, baseZ],
              [1.0, 1.0, 1.0],
              blockVisuals,
              { mass: blockMass, friction: 0.5 },
            );
            const spring = physics.addSpring(
              anchor,
              block,
              [0.0, 0.0, 0.0],
              [0.0, 0.0, 0.0],
              springStiffness,
              4.0,
            );
            springLines.addSpring(spring);
          }
        }
      } else {
        const springLines = createTrackedSpringVisualSet(sceneRoot, {
          capacity: 7,
          color: 0x9fd0ff,
        });
        trackedSpringVisualSets.push(springLines);
        const springVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
          capacity: 8,
          halfExtents: [0.5, 0.375, 0.375],
          color: 0x7f5d36,
          roughness: 0.82,
          metalness: 0.04,
        });
        trackedVisualSets.push(springVisuals);

        const bodies: number[] = [];
        const springCount = 8;
        for (let i = 0; i < springCount; i++) {
          const x = (i - (springCount - 1) * 0.5) * 3.0;
          bodies.push(addDynamicBoxWithTrackedVisual(
            [x, 12.0 + springSceneHeightOffset, 0.0],
            [0.5, 0.375, 0.375],
            springVisuals,
            { mass: (i === 0 || i === springCount - 1) ? 0.0 : 1.0, friction: 0.5 },
          ));
          if (i > 0) {
            const spring = physics.addSpring(
              bodies[i - 1]!,
              bodies[i]!,
              [0.5, 0.0, 0.0],
              [-0.5, 0.0, 0.0],
              i % 2 === 0 ? 10.0 : 10000.0,
              3.0,
            );
            springLines.addSpring(spring);
          }
        }
      }
    }

    if (isSpherePreset) {
      const largeSphereVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
        capacity: 1,
        geometry: new THREE.SphereGeometry(1.0, 28, 20),
        color: 0x9fd0ff,
        roughness: 0.58,
        metalness: 0.08,
      });
      const smallSphereVisuals = createTrackedBodyVisualSet(sceneRoot, physics, {
        capacity: 2,
        geometry: new THREE.SphereGeometry(0.75, 24, 18),
        color: 0x9fd0ff,
        roughness: 0.58,
        metalness: 0.08,
      });
      trackedVisualSets.push(largeSphereVisuals, smallSphereVisuals);

      addStaticBoxWithVisual(
        [-4.0, 1.0, 0.0],
        [2.0, 1.0, 2.0],
        { color: 0x586474 },
      );

      addDynamicSphereWithTrackedVisual(
        [-4.0, 4.6, 0.0],
        1.0,
        largeSphereVisuals,
        { mass: 2.5, friction: 0.6 },
      );
      addDynamicSphereWithTrackedVisual(
        [2.5, 1.35, 0.0],
        0.75,
        smallSphereVisuals,
        { mass: 1.5, friction: 0.6 },
      );
      addDynamicSphereWithTrackedVisual(
        [2.5, 4.6, 0.0],
        0.75,
        smallSphereVisuals,
        { mass: 1.5, friction: 0.6 },
      );
    }

    if (stackPreset === 'ragdoll-cloth-demo') {
      const ragdollClothScene = buildRagdollClothDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...ragdollClothScene.trackedVisualSets);
      trackedSpringVisualSets.push(...ragdollClothScene.trackedSpringVisualSets);
      syncedSceneVisuals.push(...ragdollClothScene.syncedVisuals);
      sceneUpdateCallbacks.push(ragdollClothScene.update);
    }

    if (stackPreset === 'ragdoll-carousel-demo') {
      const ragdollCarouselScene = buildRagdollCarouselDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
        addStaticCylinderWithVisual,
      });
      trackedVisualSets.push(...ragdollCarouselScene.trackedVisualSets);
      trackedSpringVisualSets.push(...ragdollCarouselScene.trackedSpringVisualSets);
      sceneStatusText = ragdollCarouselScene.getStatusText;
      if (ragdollCarouselScene.diagnostics.joints.length > 0) {
        carouselDiagnostics = {
          joints: ragdollCarouselScene.diagnostics.joints,
        };
      }
      sceneUpdateCallbacks.push(ragdollCarouselScene.update);
    }

    if (stackPreset === 'jointed-sandbox-demo') {
      const jointedSandboxScene = buildJointedSandboxDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...jointedSandboxScene.trackedVisualSets);
      trackedSpringVisualSets.push(...jointedSandboxScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'soft-body') {
      const softBodyScene = buildSoftBodyDemo({
        scene: sceneRoot,
        physics,
      });
      trackedVisualSets.push(...softBodyScene.trackedVisualSets);
      trackedSpringVisualSets.push(...softBodyScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'bunny-soft-body') {
      const bunnySoftBodyScene = buildBunnySoftBodyDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...bunnySoftBodyScene.trackedVisualSets);
      trackedSpringVisualSets.push(...bunnySoftBodyScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'cloth-boxes-demo') {
      const clothBoxesScene = buildClothBoxesDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...clothBoxesScene.trackedVisualSets);
      trackedSpringVisualSets.push(...clothBoxesScene.trackedSpringVisualSets);
      syncedSceneVisuals.push(...clothBoxesScene.syncedVisuals);
      sceneUpdateCallbacks.push(clothBoxesScene.update);
    }

    if (stackPreset === 'rope-demo' || stackPreset === 'heavy-rope-demo') {
      const ropeScene = buildRopeDemo({
        scene: sceneRoot,
        physics,
        variant: stackPreset,
      });
      trackedVisualSets.push(...ropeScene.trackedVisualSets);
      trackedSpringVisualSets.push(...ropeScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'coliseum-50k' || stackPreset === 'coliseum-64k') {
      const coliseumScene = buildColiseumDemo({
        scene: sceneRoot,
        physics,
        variant: stackPreset,
      });
      trackedVisualSets.push(...coliseumScene.trackedVisualSets);
      trackedSpringVisualSets.push(...coliseumScene.trackedSpringVisualSets);
      shootProjectileOverride = coliseumScene.shootProjectile;
    }

    if (stackPreset === 'dominoes-demo') {
      const dominoScene = buildDominoDemo({
        scene: sceneRoot,
        physics,
      });
      trackedVisualSets.push(...dominoScene.trackedVisualSets);
      trackedSpringVisualSets.push(...dominoScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'dominoes-advanced-demo') {
      const dominoScene = buildDominoAdvancedDemo({
        scene: sceneRoot,
        physics,
      });
      trackedVisualSets.push(...dominoScene.trackedVisualSets);
      trackedSpringVisualSets.push(...dominoScene.trackedSpringVisualSets);
    }

    if (stackPreset === 'newtons-cradles-demo') {
      const newtonsCradleScene = buildNewtonsCradleDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...newtonsCradleScene.trackedVisualSets);
      trackedSpringVisualSets.push(...newtonsCradleScene.trackedSpringVisualSets);
      sceneStatusText = () => 'AVBD + elastic impact · e=1 · 4 collision substeps';
    }

    if (stackPreset === 'ragdoll-avalanche-demo') {
      const avalancheScene = buildRagdollAvalancheDemo({
        scene: sceneRoot,
        physics,
        addStaticBoxWithVisual,
      });
      trackedVisualSets.push(...avalancheScene.trackedVisualSets);
      trackedSpringVisualSets.push(...avalancheScene.trackedSpringVisualSets);
    }

    const contactPointOverlay = new ContactPointOverlay(sceneRoot, physics.getMaxActiveContactDebugPoints());

    return {
      sceneRoot,
      spawner,
      defaultEmitter,
      trackedVisualSets,
      trackedSpringVisualSets,
      syncedSceneVisuals,
      sceneUpdateCallbacks,
      contactPointOverlay,
      bridgeMiniDiagnostics,
      carouselDiagnostics,
      sceneStatusText,
      shootProjectileOverride,
      dispose(): void {
        contactPointOverlay.dispose();
        for (const syncedVisuals of syncedSceneVisuals) {
          syncedVisuals.dispose();
        }
        for (const trackedSpringVisuals of trackedSpringVisualSets) {
          trackedSpringVisuals.dispose();
        }
        for (const trackedVisuals of trackedVisualSets) {
          trackedVisuals.dispose();
        }
        spawner.dispose();
        for (const visual of staticVisuals) {
          visual.removeFromParent();
          if (visual.geometry) {
            visual.geometry.dispose();
          }
          const material = visual.material;
          if (Array.isArray(material)) {
            for (const entry of material) {
              entry.dispose();
            }
          } else {
            material.dispose();
          }
        }
        sceneRoot.removeFromParent();
      },
    };
  };

  let activeRuntime = buildSceneRuntime();
  let perfEmitTriggered = false;
  // AO owns full-resolution color/normal/depth targets. Keep the module lazy,
  // but compile its graph against tiny valid targets before activating the
  // default-on full-size presentation targets at a drained frame boundary.
  let gtao: GTAORenderPipeline | null = null;
  let gtaoModulePromise: Promise<typeof import('./renderer/gtao')> | null = null;
  let gtaoIntensity = initialGtaoIntensity;
  let gtaoRadius = initialGtaoRadius;
  let gtaoRequestedEnabled = initialGtaoEnabled;
  let gtaoCompileAllowed = false;
  let gtaoCompilePromise: Promise<void> | null = null;
  let gtaoCompileError: string | null = null;
  let rendererPipelineMutationInProgress = false;
  let rendererPipelineMutationOwner: 'none' | 'gtao' | 'reset' = 'none';
  let resetPresentationCompilePromise: Promise<void> | null = null;
  let gtaoQueuedAfterResetPromise: Promise<void> | null = null;
  type GtaoTargetMutation = {
    mode: 'activate' | 'retire';
    notBefore: number;
  };
  let gtaoTargetMutation: GtaoTargetMutation | null = null;
  let gtaoTargetsFull = false;
  let gtaoTargetsMatchPresentation = false;
  const queueGtaoTargetMutation = (
    mode: GtaoTargetMutation['mode'],
    delayMs = mode === 'retire' ? 750 : 0,
  ): void => {
    gtaoTargetMutation = {
      mode,
      // A short quiet period coalesces rapid checkbox toggles. Normal
      // re-activation is immediate; initial compilation may request one short
      // grace window so a native startup frame is visible first. Retirement
      // can wait while the disabled graph is cost-free, then reclaim its MRT
      // at a tracked queue boundary.
      notBefore: performance.now() + Math.max(0, delayMs),
    };
    document.documentElement.dataset.gtaoTargetMutation = mode;
    if (mode === 'retire') {
      document.documentElement.dataset.gtaoResidentTargets = 'retire-pending';
    }
  };
  // AO quality follows actual scene scale, not contact-pool bytes. A compact
  // 1,000-body stack can have a deliberately generous manifold pool and still
  // deserves the full edge-aware filter, while 10k-64k stress scenes use the
  // high-resolution depth/normal-guided path. Dense scenes retain a modest
  // scale guard because their 50K-64K caster pass already dominates raster
  // work, but no tier drops below two-thirds resolution.
  const useLargeSceneGtaoProfile = isLargePresentationScene;
  const gtaoQuality = isVeryLargeScene
    ? {
        profile: 'very-large-67pct-bitmask48-fxaa-v11',
        resolutionScale: 0.67,
        samples: 48,
        detailEnabled: false,
        detailResolutionScale: 0.67,
        detailSamples: 0,
        detailRadiusScale: 0.20,
        horizonWeight: 1.0,
        jointFilterResolutionScale: 0.67,
        denoiseMode: 'edge-aware' as const,
        denoiseRadius: 4,
        detailDenoiseRadius: 2,
        postAntialias: 'fxaa' as const,
      }
    : isLargePresentationScene
      ? {
          profile: 'large-75pct-bitmask48-smaa-high-numeric-v11',
          resolutionScale: 0.75,
          samples: 48,
          detailEnabled: false,
          detailResolutionScale: 0.75,
          detailSamples: 0,
          detailRadiusScale: 0.20,
          horizonWeight: 1.0,
          jointFilterResolutionScale: 0.75,
          denoiseMode: 'edge-aware' as const,
          denoiseRadius: 4,
          detailDenoiseRadius: 2,
          postAntialias: 'smaa' as const,
        }
      : {
          profile: 'quality-100pct-bitmask72-smaa-high-numeric-v11',
          resolutionScale: 1.0,
          samples: 72,
          detailEnabled: false,
          detailResolutionScale: 1.0,
          detailSamples: 0,
          detailRadiusScale: 0.22,
          horizonWeight: 1.0,
          jointFilterResolutionScale: 1.0,
          denoiseMode: 'edge-aware' as const,
          denoiseRadius: 4,
          detailDenoiseRadius: 2,
          postAntialias: 'smaa' as const,
        };
  document.documentElement.dataset.gtaoImplementation =
    'webgpu-tsl-visibility-bitmask64-reference-gtao-v10';
  document.documentElement.dataset.gtaoPostAntialias = gtaoQuality.postAntialias;
  document.documentElement.dataset.gtaoSmaaPreset = gtaoQuality.postAntialias === 'smaa'
    ? 'high-numeric-threshold001-search16'
    : 'not-active';
  document.documentElement.dataset.gtaoQualityProfile = gtaoQuality.profile;
  document.documentElement.dataset.gtaoResolutionScale = String(gtaoQuality.resolutionScale);
  document.documentElement.dataset.gtaoSamples = String(gtaoQuality.samples);
  document.documentElement.dataset.gtaoDetailResolutionScale = String(
    gtaoQuality.detailResolutionScale,
  );
  document.documentElement.dataset.gtaoDetailSamples = String(gtaoQuality.detailSamples);
  document.documentElement.dataset.gtaoDetailEnabled = String(gtaoQuality.detailEnabled);
  document.documentElement.dataset.gtaoJointFilterResolutionScale = String(
    gtaoQuality.jointFilterResolutionScale,
  );
  document.documentElement.dataset.gtaoDenoiseMode = gtaoQuality.denoiseMode;
  document.documentElement.dataset.gtaoLargeSceneProfile = String(useLargeSceneGtaoProfile);
  document.documentElement.dataset.gtaoLargeSceneSafeDefault = 'true';
  document.documentElement.dataset.gtaoDefaultEnabled = String(defaultGtaoEnabled);
  document.documentElement.dataset.gtaoRequestedEnabled = String(gtaoRequestedEnabled);
  document.documentElement.dataset.gtaoEnabled = 'false';
  document.documentElement.dataset.gtaoAllocated = 'false';
  document.documentElement.dataset.gtaoModuleLoaded = 'false';
  document.documentElement.dataset.gtaoResidentTargets = 'none';
  document.documentElement.dataset.rendererPipelineMutationInProgress = 'false';
  document.documentElement.dataset.rendererPipelineMutationOwner = 'none';
  document.documentElement.dataset.gtaoState = gtaoRequestedEnabled ? 'waiting-first-frame' : 'disabled';

  const ensureGtao = async (): Promise<GTAORenderPipeline> => {
    if (gtao) return gtao;
    // Keep the visibility-mask GTAO/joint-filter TSL graph out of the main chunk so the
    // native scene can paint its startup UI before this graph is downloaded.
    const gtaoModule = await (gtaoModulePromise ??= import('./renderer/gtao'));
    document.documentElement.dataset.gtaoModuleLoaded = 'true';
    if (shuttingDown) {
      throw new Error('GTAO initialization cancelled during page shutdown');
    }
    if (gtao) return gtao;
    const candidate = gtaoModule.createGTAORenderPipeline(ctx.renderer, ctx.scene, ctx.camera, {
      enabled: false,
      intensity: gtaoIntensity,
      radius: gtaoRadius,
      resolutionScale: gtaoQuality.resolutionScale,
      samples: gtaoQuality.samples,
      detailEnabled: gtaoQuality.detailEnabled,
      detailResolutionScale: gtaoQuality.detailResolutionScale,
      detailSamples: gtaoQuality.detailSamples,
      detailRadiusScale: gtaoQuality.detailRadiusScale,
      horizonWeight: gtaoQuality.horizonWeight,
      jointFilterResolutionScale: gtaoQuality.jointFilterResolutionScale,
      // Persisted radius and thickness must keep the same relation across a
      // reload; a fixed 0.4 thickness made identical saved radii render
      // differently before and after refresh.
      thickness: gtaoRadius * 0.5,
      distanceFallOff: 1,
      distanceExponent: 1.8,
      denoiseMode: gtaoQuality.denoiseMode,
      denoiseRadius: gtaoQuality.denoiseRadius,
      detailDenoiseRadius: gtaoQuality.detailDenoiseRadius,
      denoiseDepthPhi: 0.18,
      denoiseNormalPhi: 24,
      postAntialias: gtaoQuality.postAntialias,
    });
    // Shader variants do not depend on target dimensions. Precompile against
    // the smallest valid targets; allocating the full MRT before compilation
    // made every 50K/64K scene appear frozen before its first present.
    try {
      candidate.resize(1, 1, 1);
    } catch (error) {
      candidate.dispose();
      throw error;
    }
    // Publish only a fully constructed, valid tiny-target pipeline. A private
    // Three ABI/resize failure can then retry instead of reusing a partial
    // global instance on the next enable request.
    gtao = candidate;
    gtaoTargetsFull = false;
    gtaoTargetsMatchPresentation = false;
    document.documentElement.dataset.gtaoAllocated = 'true';
    document.documentElement.dataset.gtaoResidentTargets = '1x1';
    return gtao;
  };

  const queueGtaoAfterReset = (): Promise<void> => {
    if (!resetPresentationCompilePromise) return Promise.resolve();
    if (gtaoQueuedAfterResetPromise) return gtaoQueuedAfterResetPromise;
    const resetBarrier = resetPresentationCompilePromise;
    const queued = resetBarrier.then(async () => {
      if (shuttingDown) return;
      // Read the latest requested value: repeated toggles during reset collapse
      // into one deterministic action after the renderer mutation barrier.
      setGtaoEnabled(gtaoRequestedEnabled);
      if (gtaoCompilePromise) await gtaoCompilePromise;
    }).catch((error: unknown) => {
      if (!shuttingDown) console.error('[GTAO] queued post-reset update failed:', error);
    }).finally(() => {
      if (gtaoQueuedAfterResetPromise === queued) gtaoQueuedAfterResetPromise = null;
    });
    gtaoQueuedAfterResetPromise = queued;
    return queued;
  };

  const startGtaoCompile = (): Promise<void> => {
    if (resetPresentationCompilePromise) {
      document.documentElement.dataset.gtaoState = 'queued-after-reset';
      return queueGtaoAfterReset();
    }
    if (gtao?.isCompiled && gtaoCompileError === null) return Promise.resolve();
    if (gtaoCompilePromise) return gtaoCompilePromise;
    // Three's async compiler mutates renderer render-target/MRT/node state
    // across awaits. Keep the rAF/DOM loop alive, but gate all GPU encoding
    // until that exact state is restored; otherwise a runtime AO toggle can
    // race a physics/render callback and send a frame to the AO target.
    rendererPipelineMutationInProgress = true;
    rendererPipelineMutationOwner = 'gtao';
    document.documentElement.dataset.rendererPipelineMutationInProgress = 'true';
    document.documentElement.dataset.rendererPipelineMutationOwner = 'gtao';
    document.documentElement.dataset.gtaoState = 'compiling';
    // Yield one browser frame before shader graph construction. This lets the
    // canvas/status UI paint even when the driver has a completely cold cache.
    gtaoCompilePromise = new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    }).then(async () => {
      const pipeline = await ensureGtao();
      await pipeline.compileAsync();
    }).then(() => {
      gtaoCompileError = null;
      delete document.documentElement.dataset.gtaoCompileError;
      if (gtaoRequestedEnabled) {
        const pipeline = gtao;
        if (!pipeline) throw new Error('GTAO module loaded without creating its render pipeline');
        pipeline.enabled = false;
        presentationDirty = true;
        document.documentElement.dataset.gtaoEnabled = 'false';
        document.documentElement.dataset.gtaoState = 'activating-targets';
        // Let at least one native scene frame reach the canvas before the
        // full-resolution MRT allocation. The request remains default-on and
        // activates automatically at the queue-drained mutation gate.
        queueGtaoTargetMutation('activate', 80);
      } else {
        if (gtao) {
          gtao.enabled = false;
          queueGtaoTargetMutation('retire');
        }
        document.documentElement.dataset.gtaoState = 'disabled';
      }
    }).catch((error: unknown) => {
      if (shuttingDown) return;
      gtaoCompileError = error instanceof Error ? error.message : String(error);
      if (gtao) {
        gtao.enabled = false;
        queueGtaoTargetMutation('retire');
      }
      document.documentElement.dataset.gtaoEnabled = 'false';
      document.documentElement.dataset.gtaoState = 'failed';
      document.documentElement.dataset.gtaoCompileError = gtaoCompileError;
      console.error('[GTAO] async pipeline compilation failed:', error);
    }).finally(() => {
      if (rendererPipelineMutationOwner === 'gtao') {
        rendererPipelineMutationOwner = 'none';
        rendererPipelineMutationInProgress = false;
        if (!shuttingDown) {
          document.documentElement.dataset.rendererPipelineMutationInProgress = 'false';
          document.documentElement.dataset.rendererPipelineMutationOwner = 'none';
          presentationDirty = true;
          if (physicsStartupReady) {
            queuedSimulationDt = 0.0;
            timer.reset();
          }
        }
      }
      gtaoCompilePromise = null;
    });
    return gtaoCompilePromise;
  };

  function setGtaoEnabled(enabled: boolean): void {
    gtaoRequestedEnabled = Boolean(enabled);
    presentationDirty = true;
    document.documentElement.dataset.gtaoRequestedEnabled = String(gtaoRequestedEnabled);
    if (resetPresentationCompilePromise) {
      // compileAsync and GTAO both temporarily mutate renderer target/MRT/color
      // state. Never let a UI toggle overlap Reset's scene compilation.
      if (gtao) gtao.enabled = false;
      document.documentElement.dataset.gtaoEnabled = 'false';
      document.documentElement.dataset.gtaoState = 'queued-after-reset';
      void queueGtaoAfterReset();
      return;
    }
    if (!gtaoRequestedEnabled) {
      if (gtao) {
        gtao.enabled = false;
        if (!gtaoCompilePromise) queueGtaoTargetMutation('retire');
      }
      document.documentElement.dataset.gtaoEnabled = 'false';
      document.documentElement.dataset.gtaoState = 'disabled';
      return;
    }
    if (gtaoTargetMutation?.mode === 'retire') {
      gtaoTargetMutation = null;
      delete document.documentElement.dataset.gtaoTargetMutation;
    }
    if (gtao?.isCompiled && gtaoCompileError === null) {
      if (gtaoTargetsFull && gtaoTargetsMatchPresentation) {
        gtao.enabled = true;
        document.documentElement.dataset.gtaoEnabled = 'true';
        document.documentElement.dataset.gtaoResidentTargets = 'full';
        document.documentElement.dataset.gtaoState = 'ready';
      } else {
        gtao.enabled = false;
        document.documentElement.dataset.gtaoEnabled = 'false';
        document.documentElement.dataset.gtaoState = 'activating-targets';
        queueGtaoTargetMutation('activate');
      }
    } else if (!gtaoCompileAllowed) {
      document.documentElement.dataset.gtaoEnabled = 'false';
      document.documentElement.dataset.gtaoState = 'queued-after-scene';
    } else {
      document.documentElement.dataset.gtaoEnabled = 'false';
      void startGtaoCompile();
    }
  }

  setGtaoEnabled(initialGtaoEnabled);
  let presentationPipelinesReady = false;
  let presentationPipelineCompileFailed = false;
  document.documentElement.dataset.presentationPipelineState = 'queued';
  setStartupStatus('render-pipelines', '正在异步编译渲染管线…');
  const presentationPrewarmPromise = new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  }).then(async () => {
    document.documentElement.dataset.presentationPipelineState = 'compiling-scene';
    await ctx.renderer.compileAsync(ctx.scene, ctx.camera);
    if (shuttingDown) return;
    presentationPipelinesReady = true;
    presentationDirty = true;
    document.documentElement.dataset.presentationPipelineState = 'initial-scene-ready';
  }).catch((error: unknown) => {
    if (shuttingDown) return;
    presentationPipelineCompileFailed = true;
    const message = error instanceof Error ? error.message : String(error);
    document.documentElement.dataset.presentationPipelineState = 'failed';
    document.documentElement.dataset.presentationPipelineError = message;
    setStartupStatus('failed', `渲染管线编译失败：${message}`);
    console.error('[Startup] async presentation pipeline compilation failed:', error);
  });
  let physicsStartupReady = false;
  document.documentElement.dataset.physicsPipelineState = 'queued';
  const physicsPrewarmPromise = presentationPrewarmPromise.then(async () => {
    if (shuttingDown || presentationPipelineCompileFailed) return;
    setStartupStatus('physics-pipelines', '正在异步预热 AVBD compute 管线…');
    document.documentElement.dataset.physicsPipelineState = 'collecting';
    const result = await physics.prewarmStartupPipelines(
      ctx.renderer,
      (completedNodes, totalNodes) => {
        document.documentElement.dataset.physicsPipelineState = 'compiling';
        document.documentElement.dataset.physicsPipelineNodesCompleted = String(completedNodes);
        document.documentElement.dataset.physicsPipelineNodesTotal = String(totalNodes);
        setStartupStatus(
          'physics-pipelines',
          `正在异步预热 AVBD compute 管线… ${completedNodes}/${totalNodes}`,
        );
      },
    );
    if (shuttingDown) return;
    document.documentElement.dataset.physicsPipelineCommandCount = String(result.commandCount);
    document.documentElement.dataset.physicsPipelineUniqueNodeCount = String(result.uniqueNodeCount);
    if (!result.ok) {
      document.documentElement.dataset.physicsPipelineState = 'failed';
      document.documentElement.dataset.physicsPipelineError = result.reason ?? 'unknown';
      setStartupStatus('failed', `AVBD compute 管线预热失败：${result.reason ?? 'unknown'}`);
      return;
    }
    // initGPU publishes the storage buffers consumed by shader-driven body
    // visuals. Bind those nodes before declaring startup complete, then compile
    // the *actual* presentation materials asynchronously. Previously this
    // happened inside the first physical frame, invalidating the initial render
    // pipelines and causing the visible 100-200 ms first-step hitch on 50K/64K.
    presentationPipelinesReady = false;
    document.documentElement.dataset.presentationPipelineState = 'binding-physics-visuals';
    activeRuntime.spawner.syncVisuals(physics);
    for (const trackedVisuals of activeRuntime.trackedVisualSets) {
      trackedVisuals.syncVisuals(physics);
    }
    for (const trackedSpringVisuals of activeRuntime.trackedSpringVisualSets) {
      trackedSpringVisuals.syncVisuals(physics);
    }
    for (const syncedVisuals of activeRuntime.syncedSceneVisuals) {
      syncedVisuals.syncVisuals(physics);
    }
    setStartupStatus('render-pipelines', '正在异步编译物理绑定后的渲染管线…');
    document.documentElement.dataset.presentationPipelineState = 'compiling-bound-scene';
    await ctx.renderer.compileAsync(ctx.scene, ctx.camera);
    if (shuttingDown) return;
    gtaoCompileAllowed = true;
    if (gtaoRequestedEnabled) {
      document.documentElement.dataset.presentationPipelineState = 'compiling-gtao';
      await startGtaoCompile();
      if (shuttingDown) return;
    }
    presentationPipelinesReady = true;
    document.documentElement.dataset.presentationPipelineState = 'ready';
    // Startup wall time is not simulation time. Beginning with the capped
    // 50 ms backlog would kick resting stacks and make the first post-compile
    // frame artificially expensive.
    queuedSimulationDt = 0.0;
    timer.reset();
    physicsStartupReady = true;
    startupPhysicsReadyObserved = true;
    presentationDirty = true;
    document.documentElement.dataset.physicsPipelineState = 'ready';
    document.documentElement.dataset.startupReadyMs = (
      performance.now() - startupStartedAt
    ).toFixed(3);
    setStartupStatus('ready', gtao?.enabled ? 'AVBD 与 GTAO 已就绪' : 'AVBD 管线已就绪');
    window.setTimeout(() => {
      if (startupStatus.isConnected) {
        startupStatus.style.opacity = '0';
        window.setTimeout(() => startupStatus.remove(), 220);
      }
    }, 450);
  }).catch((error: unknown) => {
    if (shuttingDown) return;
    const message = error instanceof Error ? error.message : String(error);
    physicsStartupReady = false;
    presentationPipelinesReady = false;
    document.documentElement.dataset.physicsPipelineState = 'failed';
    document.documentElement.dataset.physicsPipelineError = message;
    document.documentElement.dataset.presentationPipelineState = 'failed';
    document.documentElement.dataset.presentationPipelineError = message;
    setStartupStatus('failed', `启动管线编译失败：${message}`);
    console.error('[Startup] async final pipeline compilation failed:', error);
  });
  void physicsPrewarmPromise;

  // Debug
  const overlay = new DebugOverlay();
  const gpuPassTimer = new GpuPassTimer(ctx.device, ctx.renderer);
  physics.setNativeComputePassDescriptorFactory(
    (label) => gpuPassTimer.createNativeComputePassDescriptor(label),
  );
  const defaultDebugTimingEnabled = false;
  const defaultDebugBehaviorEnabled = false;
  const defaultShowContactPoints = false;
  const defaultSimulationPaused = false;
  const defaultLogEveryStep = false;
  const defaultTimeScale = 1.0;
  const forceHighCapacityDebugOff = isHighCapacityScene;
  const debugTimingEnabledByDefault = forceHighCapacityDebugOff
    ? false
    : loadDebugTimingEnabled(defaultDebugTimingEnabled);
  const debugBehaviorEnabledByDefault = forceHighCapacityDebugOff
    ? false
    : loadDebugBehaviorEnabled(defaultDebugBehaviorEnabled);
  const initialShowContactPoints = forceHighCapacityDebugOff
    ? false
    : loadShowContactPoints(defaultShowContactPoints);
  if (forceHighCapacityDebugOff) {
    console.info(
      `[AVBD Debug] Persisted heavy diagnostics disabled for high-capacity workload `
      + `(contactRecords=${contactRecordCapacity}).`,
    );
  }
  let debugBehaviorEnabled = debugBehaviorEnabledByDefault;
  let showContactPointsEnabled = initialShowContactPoints;
  let logEveryStepEnabled = initialLogEveryStep;
  const setDebugTimingEnabled = (enabled: boolean, persist = true): void => {
    gpuPassTimer.setEnabled(enabled);
    if (persist) persistDebugTimingEnabled(enabled);
  };
  const setDebugBehaviorEnabled = (enabled: boolean, persist = true): void => {
    const effectiveEnabled = enabled && !forceHighCapacityDebugOff;
    debugBehaviorEnabled = effectiveEnabled;
    physics.setDebugBehaviorEnabled(effectiveEnabled);
    if (persist && !forceHighCapacityDebugOff) {
      persistDebugBehaviorEnabled(effectiveEnabled);
    }
  };
  setDebugTimingEnabled(debugTimingEnabledByDefault, false);
  setDebugBehaviorEnabled(debugBehaviorEnabledByDefault, false);
  const setShowContactPoints = (enabled: boolean, persist = true): void => {
    presentationDirty = true;
    showContactPointsEnabled = enabled;
    activeRuntime.contactPointOverlay.setEnabled(enabled);
    if (persist) persistShowContactPoints(enabled);
  };
  setShowContactPoints(initialShowContactPoints, false);
  let timeScale = defaultTimeScale;
  let simulationPaused = initialSimulationPaused;
  let pendingSimulationSteps = 0;
  let pendingManualStepSceneUpdateApplied = false;
  // Keep the WebGPU queue shallow.  WebGPU submissions are asynchronous, so
  // an unrestricted rAF loop can enqueue 60+ heavy Coliseum steps per second
  // even when the adapter completes only ~40.  That backlog both increases
  // latency without bound and survives a scene switch on the same device.
  // Tiny worlds need a deeper ring to hide browser/presentation fence latency.
  // Heavy worlds retain a strict four-frame ceiling: enough to overlap command
  // preparation, but still bounded instead of the former unbounded backlog.
  const getMaxGpuFramesInFlight = (): number => {
    const bodyCount = physics.getBodyCount();
    // A 10x10x10 stack has 1,000 dynamic bodies plus its floor.  Treating
    // that single extra static body as a "large" world cut the queue ring in
    // half and left the adapter idle while rAF waited for fence callbacks.
    // Keep the deeper latency-hiding ring through one thousand dynamic bodies;
    // the strict four-frame ceiling remains in place for the stress scenes
    // where every queued frame is expensive.
    return bodyCount <= 1024 ? 8 : 4;
  };
  document.documentElement.dataset.presentationReservedFrameSlots = '1';
  const MAX_PRESENTATIONS_IN_FLIGHT = 2;
  document.documentElement.dataset.gpuPresentationFrameLimit =
    String(MAX_PRESENTATIONS_IN_FLIGHT);
  let gpuFramesInFlight = 0;
  let gpuPresentationsInFlight = 0;
  let gpuSubmissionSerial = 0;
  let gpuVisibilityEpoch = 0;
  const pendingGpuSubmissions = new Map<number, number>();
  const pendingPresentationSubmissions = new Set<number>();
  let lastGpuCompletionAt = 0.0;
  let gpuFenceDurationEmaMs = 0.0;
  let visiblePendingSince = 0.0;
  let lastGpuTelemetryPublishedAt = Number.NEGATIVE_INFINITY;
  let lastPhysicsTelemetryPublishedAt = Number.NEGATIVE_INFINITY;
  const GPU_QUEUE_WATCHDOG_FLOOR_MS = 15_000;
  const GPU_QUEUE_WATCHDOG_CEILING_MS = 60_000;
  let skippedGpuSubmissionFrames = 0;
  let queuedSimulationDt = 0.0;
  let resetRequested = false;
  let pendingManualEmitCount = 0;
  let pendingImmediateShots = 0;
  let capacityDiagnosticsInFlight = false;
  let capacityDiagnosticsCaptured = false;
  let capacityDiagnosticsGeneration = 0;
  let capacitySpawningStopped = false;
  let emitterPanel: EmitterPanel;
  let fps: FPSController;

  const applyPendingPresentationResize = (): boolean => {
    const pending = pendingPresentationResize;
    if (
      !pending
      || performance.now() < pending.applyAfter
      || gpuFramesInFlight !== 0
      || !presentationPipelinesReady
      || gpuRecoveryScheduled
      || gpuValidationSubmissionHalted
      || rendererPipelineMutationInProgress
      || resetRequested
      || capacityDiagnosticsInFlight
      || bridgeMiniDiagnosticsInFlight
      || carouselDiagnosticsInFlight
    ) {
      return false;
    }

    // Consume only the newest resize after the queue has drained. CSS keeps
    // stretching the last valid canvas frame until this atomic backing-store,
    // camera and post-process resize is immediately followed by a render.
    pendingPresentationResize = null;
    appliedPresentationWidth = pending.width;
    appliedPresentationHeight = pending.height;
    appliedPresentationPixelRatio = pending.pixelRatio;
    ctx.camera.aspect = pending.width / pending.height;
    ctx.camera.updateProjectionMatrix();
    // setDrawingBufferSize updates logical dimensions, DPR, backing store and
    // viewport in one operation. setPixelRatio()+setSize() would clear/recreate
    // the canvas twice with an obsolete intermediate size.
    ctx.renderer.setDrawingBufferSize(pending.width, pending.height, pending.pixelRatio);
    if (gtao) {
      if (gtao.enabled) {
        gtao.resize(pending.width, pending.height, pending.pixelRatio);
        gtaoTargetsFull = true;
        gtaoTargetsMatchPresentation = true;
        document.documentElement.dataset.gtaoResidentTargets = 'full';
      } else if (gtaoTargetsFull) {
        // A disabled target does not need a costly resize. Mark it stale; an
        // activation request will resize it at the same drained mutation gate,
        // while the delayed retirement normally collapses it to 1x1 first.
        gtaoTargetsMatchPresentation = false;
      }
    }
    presentationDirty = true;
    forcedPresentationFrames = Math.max(forcedPresentationFrames, 2);
    // Window-management time is not simulation time. Never feed a queued 50ms
    // catch-up step into a resting stack after resize backpressure drains.
    queuedSimulationDt = 0.0;
    timer.reset();
    document.documentElement.dataset.presentationResizeState = 'applied';
    document.documentElement.dataset.presentationResizeAppliedSerial = String(pending.serial);
    return true;
  };

  const applyPendingGtaoTargetMutation = (): boolean => {
    const mutation = gtaoTargetMutation;
    if (
      !mutation
      || performance.now() < mutation.notBefore
      || !gtao
      || gtaoCompilePromise !== null
      || pendingPresentationResize !== null
      || gpuFramesInFlight !== 0
      || rendererPipelineMutationInProgress
      || resetRequested
      || capacityDiagnosticsInFlight
      || bridgeMiniDiagnosticsInFlight
      || carouselDiagnosticsInFlight
    ) {
      return false;
    }

    if (
      (mutation.mode === 'retire' && gtaoRequestedEnabled)
      || (mutation.mode === 'activate' && !gtaoRequestedEnabled)
    ) {
      gtaoTargetMutation = null;
      delete document.documentElement.dataset.gtaoTargetMutation;
      return false;
    }

    if (mutation.mode === 'activate') {
      if (!gtao.isCompiled || gtaoCompileError !== null) {
        gtaoTargetMutation = null;
        delete document.documentElement.dataset.gtaoTargetMutation;
        return false;
      }
      gtao.resize(
        appliedPresentationWidth,
        appliedPresentationHeight,
        appliedPresentationPixelRatio,
      );
      gtaoTargetsFull = true;
      gtaoTargetsMatchPresentation = true;
      gtao.enabled = true;
      document.documentElement.dataset.gtaoEnabled = 'true';
      document.documentElement.dataset.gtaoResidentTargets = 'full';
      document.documentElement.dataset.gtaoState = 'ready';
    } else {
      gtao.enabled = false;
      gtao.resize(1, 1, 1);
      gtaoTargetsFull = false;
      gtaoTargetsMatchPresentation = false;
      document.documentElement.dataset.gtaoEnabled = 'false';
      document.documentElement.dataset.gtaoResidentTargets = '1x1';
      document.documentElement.dataset.gtaoState = 'disabled';
    }

    gtaoTargetMutation = null;
    delete document.documentElement.dataset.gtaoTargetMutation;
    presentationDirty = true;
    forcedPresentationFrames = Math.max(forcedPresentationFrames, 1);
    queuedSimulationDt = 0.0;
    timer.reset();
    return true;
  };

  const gpuQueueWatchdogThresholdMs = (): number => Math.min(
    GPU_QUEUE_WATCHDOG_CEILING_MS,
    Math.max(GPU_QUEUE_WATCHDOG_FLOOR_MS, gpuFenceDurationEmaMs * 20.0),
  );

  const updateGpuQueueTelemetry = (now: number, maxGpuFramesInFlight: number): void => {
    let oldestStartedAt = Number.POSITIVE_INFINITY;
    for (const startedAt of pendingGpuSubmissions.values()) {
      oldestStartedAt = Math.min(oldestStartedAt, startedAt);
    }
    const hasPendingSubmissions = Number.isFinite(oldestStartedAt);
    const oldestAgeMs = hasPendingSubmissions ? Math.max(0.0, now - oldestStartedAt) : 0.0;
    const thresholdMs = gpuQueueWatchdogThresholdMs();

    if (document.visibilityState !== 'visible' || !hasPendingSubmissions) {
      visiblePendingSince = 0.0;
    } else if (visiblePendingSince === 0.0) {
      // A tab returning from the background gets a full watchdog grace period;
      // background throttling must not be mistaken for a wedged GPU queue.
      visiblePendingSince = now;
    }
    const visiblePendingAgeMs = visiblePendingSince > 0.0 ? Math.max(0.0, now - visiblePendingSince) : 0.0;

    // Dataset writes are public diagnostics, not part of the solver's hot path.
    // Publishing four times per second avoids adding DOM mutation overhead to
    // every high-capacity frame while preserving useful watchdog telemetry.
    if (now - lastGpuTelemetryPublishedAt >= 250.0) {
      lastGpuTelemetryPublishedAt = now;
      document.documentElement.dataset.gpuFramesInFlight = String(gpuFramesInFlight);
      document.documentElement.dataset.gpuPresentationsInFlight =
        String(gpuPresentationsInFlight);
      document.documentElement.dataset.gpuFrameLimit = String(maxGpuFramesInFlight);
      document.documentElement.dataset.gpuSubmissionSkippedFrames = String(skippedGpuSubmissionFrames);
      document.documentElement.dataset.gpuOldestSubmissionAgeMs = oldestAgeMs.toFixed(1);
      document.documentElement.dataset.gpuVisiblePendingAgeMs = visiblePendingAgeMs.toFixed(1);
      document.documentElement.dataset.gpuLastCompletionAgeMs = lastGpuCompletionAt > 0.0
        ? Math.max(0.0, now - lastGpuCompletionAt).toFixed(1)
        : '-1';
      document.documentElement.dataset.gpuFenceEmaMs = gpuFenceDurationEmaMs.toFixed(1);
      document.documentElement.dataset.gpuQueueWatchdogThresholdMs = thresholdMs.toFixed(1);
      document.documentElement.dataset.gpuResetPending = String(resetRequested);

      if (!gpuRecoveryScheduled && !gpuValidationSubmissionHalted) {
        document.documentElement.dataset.gpuQueueState = resetRequested && hasPendingSubmissions
          ? 'draining-reset'
          : gpuFramesInFlight >= maxGpuFramesInFlight
            ? 'backpressure'
            : 'healthy';
      }
    }

    if (
      !gpuRecoveryScheduled
      && document.visibilityState === 'visible'
      && hasPendingSubmissions
      && oldestAgeMs >= thresholdMs
      && visiblePendingAgeMs >= thresholdMs
    ) {
      scheduleGpuRecovery(
        `queue-timeout:${Math.round(oldestAgeMs)}ms-oldest/${gpuFramesInFlight}-in-flight`,
      );
    }
  };

  const registerGpuSubmissionFence = (presented: boolean): void => {
    const submissionId = ++gpuSubmissionSerial;
    const startedAt = performance.now();
    const submissionVisibilityEpoch = gpuVisibilityEpoch;
    const submissionStartedVisible = document.visibilityState === 'visible';
    pendingGpuSubmissions.set(submissionId, startedAt);
    if (presented) pendingPresentationSubmissions.add(submissionId);
    gpuFramesInFlight = pendingGpuSubmissions.size;
    gpuPresentationsInFlight = pendingPresentationSubmissions.size;
    if (document.visibilityState === 'visible' && visiblePendingSince === 0.0) {
      visiblePendingSince = startedAt;
    }

    try {
      const fence = ctx.device.queue.onSubmittedWorkDone();
      void fence.then(() => {
        if (!pendingGpuSubmissions.delete(submissionId)) return;
        pendingPresentationSubmissions.delete(submissionId);
        const completedAt = performance.now();
        const durationMs = Math.max(0.0, completedAt - startedAt);
        // Browser background throttling is not GPU execution time. Ignore a
        // fence for threshold learning if visibility changed anywhere across
        // its lifetime, otherwise one hidden interval can inflate a later real
        // hang's watchdog threshold all the way to 60 seconds.
        if (
          submissionStartedVisible
          && document.visibilityState === 'visible'
          && submissionVisibilityEpoch === gpuVisibilityEpoch
        ) {
          gpuFenceDurationEmaMs = gpuFenceDurationEmaMs === 0.0
            ? durationMs
            : gpuFenceDurationEmaMs * 0.9 + durationMs * 0.1;
        }
        lastGpuCompletionAt = completedAt;
        gpuFramesInFlight = pendingGpuSubmissions.size;
        gpuPresentationsInFlight = pendingPresentationSubmissions.size;
        if (gpuFramesInFlight === 0) visiblePendingSince = 0.0;
      }, (error: unknown) => {
        // Keep this submission recorded as pending. A rejected fence does not
        // prove that its GPU work completed, so resource reset/reuse is unsafe.
        const message = error instanceof Error ? error.message : String(error);
        document.documentElement.dataset.gpuFenceFailure = message;
        scheduleGpuRecovery(`fence-rejected:${message}`);
      });
    } catch (error) {
      // Some implementations throw synchronously after loss/destruction rather
      // than returning a rejected promise. Treat both forms identically.
      const message = error instanceof Error ? error.message : String(error);
      document.documentElement.dataset.gpuFenceFailure = message;
      scheduleGpuRecovery(`fence-threw:${message}`);
    }
  };

  document.addEventListener('visibilitychange', () => {
    gpuVisibilityEpoch++;
    if (document.visibilityState === 'visible') {
      // Re-sample both viewport and DPR after a monitor move or restore; the
      // hidden transition may only have reported an unusable 0/1px size.
      queuePresentationResize();
      presentationDirty = true;
      forcedPresentationFrames = Math.max(forcedPresentationFrames, 2);
    }
    visiblePendingSince = document.visibilityState === 'visible' && pendingGpuSubmissions.size > 0
      ? performance.now()
      : 0.0;
  });
  window.addEventListener('focus', () => {
    presentationDirty = true;
    forcedPresentationFrames = Math.max(forcedPresentationFrames, 2);
  });
  // Presentation/rAF can stop independently of the JavaScript task queue.
  // Keep a separate low-frequency heartbeat so pending fences still trigger
  // device replacement when no animation callback arrives.
  const gpuQueueWatchdogTimer = window.setInterval(() => {
    updateGpuQueueTelemetry(performance.now(), getMaxGpuFramesInFlight());
  }, 1000);

  const syncActiveRuntimeVisuals = (): void => {
    activeRuntime.spawner.syncVisuals(physics);
    for (const trackedVisuals of activeRuntime.trackedVisualSets) {
      trackedVisuals.syncVisuals(physics);
    }
    for (const trackedSpringVisuals of activeRuntime.trackedSpringVisualSets) {
      trackedSpringVisuals.syncVisuals(physics);
    }
    for (const syncedVisuals of activeRuntime.syncedSceneVisuals) {
      syncedVisuals.syncVisuals(physics);
    }
  };

  let resetPresentationCompileGeneration = 0;
  const prewarmResetPresentation = (): void => {
    const generation = ++resetPresentationCompileGeneration;
    rendererPipelineMutationInProgress = true;
    rendererPipelineMutationOwner = 'reset';
    presentationPipelinesReady = false;
    document.documentElement.dataset.rendererPipelineMutationInProgress = 'true';
    document.documentElement.dataset.rendererPipelineMutationOwner = 'reset';
    document.documentElement.dataset.presentationPipelineState = 'compiling-reset-scene';

    const pending: Promise<void> = (async () => {
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (shuttingDown || generation !== resetPresentationCompileGeneration) return;
      gtao?.prepareNativeSceneCompile();
      await ctx.renderer.compileAsync(ctx.scene, ctx.camera);
      if (shuttingDown || generation !== resetPresentationCompileGeneration) return;
      if (gtao?.isCompiled) {
        document.documentElement.dataset.presentationPipelineState = 'compiling-reset-gtao';
        await gtao.compileSceneAsync();
        if (shuttingDown || generation !== resetPresentationCompileGeneration) return;
      }
      presentationPipelinesReady = true;
      presentationDirty = true;
      document.documentElement.dataset.presentationPipelineState = 'ready';
    })().catch((error: unknown) => {
      if (shuttingDown || generation !== resetPresentationCompileGeneration) return;
      const message = error instanceof Error ? error.message : String(error);
      presentationPipelinesReady = false;
      document.documentElement.dataset.presentationPipelineState = 'failed';
      document.documentElement.dataset.presentationPipelineError = message;
      console.error('[Reset] async presentation pipeline compilation failed:', error);
    }).finally(() => {
      if (generation !== resetPresentationCompileGeneration) return;
      if (!shuttingDown) gtao?.restorePresentationOverlayMrt();
      if (resetPresentationCompilePromise === pending) {
        resetPresentationCompilePromise = null;
      }
      if (rendererPipelineMutationOwner === 'reset') {
        rendererPipelineMutationOwner = 'none';
        rendererPipelineMutationInProgress = false;
        if (!shuttingDown) {
          document.documentElement.dataset.rendererPipelineMutationInProgress = 'false';
          document.documentElement.dataset.rendererPipelineMutationOwner = 'none';
          queuedSimulationDt = 0.0;
          timer.reset();
        }
      }
    });
    resetPresentationCompilePromise = pending;
  };

  const resetSimulation = (): void => {
    presentationDirty = true;
    const fpsState: FPSControllerState = fps.captureState();
    fireAccumulator = 0.0;
    triggerHeld = false;
    pendingSimulationSteps = 0;
    pendingManualStepSceneUpdateApplied = false;
    queuedSimulationDt = 0.0;
    capacityDiagnosticsInFlight = false;
    capacityDiagnosticsCaptured = false;
    capacityDiagnosticsGeneration++;
    capacitySpawningStopped = false;
    delete document.documentElement.dataset.physicsCapacityDiagnostics;
    delete document.documentElement.dataset.physicsCapacitySpawningStopped;
    bridgeMiniDiagnosticsInFlight = false;
    lastBridgeMiniDiagnosticsFrame = -1;
    carouselDiagnosticsInFlight = false;
    lastCarouselDiagnosticsFrame = -1;
    activeRuntime.dispose();
    physics.clearScene();
    activeRuntime = buildSceneRuntime();
    referenceLighting.invalidateShadow();
    shadowSceneDirty = true;
    syncActiveRuntimeVisuals();
    prewarmResetPresentation();
    perfEmitTriggered = false;
    fps.restoreState(fpsState);
    fps.setSceneStatusText(activeRuntime.sceneStatusText?.() ?? null);
    activeRuntime.contactPointOverlay.setEnabled(showContactPointsEnabled);
    emitterPanel.setSpawner(activeRuntime.spawner);
    (window as typeof window & { __WEBPHYS_BRIDGE_MINI_DEBUG__?: unknown }).__WEBPHYS_BRIDGE_MINI_DEBUG__ = undefined;
    (window as typeof window & { __WEBPHYS_DRIVEN_SEAT_DEBUG__?: unknown }).__WEBPHYS_DRIVEN_SEAT_DEBUG__ = undefined;
    (window as typeof window & { __WEBPHYS_CAROUSEL_DEBUG__?: unknown }).__WEBPHYS_CAROUSEL_DEBUG__ = undefined;
  };
  const requestSimulationReset = (): void => {
    // clearScene() destroys and recreates large GPU resources.  It must never
    // race work that still references the old buffers, so the animation loop
    // stops submitting and performs the reset only after the frame ring drains.
    resetRequested = true;
    presentationDirty = true;
    pendingManualEmitCount = 0;
    pendingImmediateShots = 0;
    pendingSimulationSteps = 0;
    pendingManualStepSceneUpdateApplied = false;
    queuedSimulationDt = 0.0;
    triggerHeld = false;
    fireAccumulator = 0.0;
  };
  const panelDefaults: EmitterResetDefaults = {
    enabled: activeRuntime.defaultEmitter.enabled,
    rate: activeRuntime.defaultEmitter.rate,
    x: activeRuntime.defaultEmitter.x,
    y: activeRuntime.defaultEmitter.y,
    z: activeRuntime.defaultEmitter.z,
    randomRotation: activeRuntime.defaultEmitter.randomRotation,
    rotationJitter: activeRuntime.defaultEmitter.rotationJitter,
    debugTimingEnabled: defaultDebugTimingEnabled,
    debugBehaviorEnabled: defaultDebugBehaviorEnabled,
    showContactPoints: defaultShowContactPoints,
    shadowsEnabled: defaultShadowsEnabled,
    gtaoEnabled: defaultGtaoEnabled,
    gtaoIntensity: defaultGtaoIntensity,
    gtaoRadius: defaultGtaoRadius,
    paused: defaultSimulationPaused,
    logEveryStep: defaultLogEveryStep,
    timeScale: defaultTimeScale,
    friction: initialFriction,
    dualUpdateBeta: defaultDualUpdateBeta,
    angularDualUpdateBeta: defaultAngularDualUpdateBeta,
    regularizationAlpha: defaultRegularizationAlpha,
    penaltyDecayGamma: defaultPenaltyDecayGamma,
    solverIterations: defaultSolverIterations,
    stackPreset,
  };
  emitterPanel = new EmitterPanel(activeRuntime.spawner, ctx.renderer, {
    debugTimingEnabled: debugTimingEnabledByDefault,
    onDebugTimingEnabledChange: (enabled) => setDebugTimingEnabled(enabled),
    debugBehaviorEnabled: debugBehaviorEnabledByDefault,
    debugBehaviorLockedOff: forceHighCapacityDebugOff,
    onDebugBehaviorEnabledChange: (enabled) => setDebugBehaviorEnabled(enabled),
    showContactPoints: initialShowContactPoints,
    onShowContactPointsChange: (enabled) => {
      setShowContactPoints(enabled);
    },
    shadowsEnabled: initialShadowsEnabled,
    onShadowsEnabledChange: (enabled) => {
      setShadowsEnabled(enabled);
      persistShadowsEnabled(enabled);
    },
    gtaoEnabled: initialGtaoEnabled,
    onGtaoEnabledChange: (enabled) => {
      setGtaoEnabled(enabled);
      persistGtaoEnabled(enabled);
    },
    gtaoIntensity: initialGtaoIntensity,
    onGtaoIntensityChange: (intensity) => {
      gtaoIntensity = intensity;
      if (gtao) gtao.intensity = intensity;
      presentationDirty = true;
      persistGtaoScalar(GTAO_INTENSITY_STORAGE_KEY, intensity, 0.0, 10.0);
    },
    gtaoRadius: initialGtaoRadius,
    onGtaoRadiusChange: (radius) => {
      gtaoRadius = radius;
      if (gtao) gtao.radius = radius;
      presentationDirty = true;
      persistGtaoScalar(GTAO_RADIUS_STORAGE_KEY, radius, 0.1, 10.0);
    },
    paused: initialSimulationPaused,
    onPausedChange: (paused) => {
      simulationPaused = gpuValidationSubmissionHalted ? true : paused;
      if (!simulationPaused) {
        pendingSimulationSteps = 0;
        pendingManualStepSceneUpdateApplied = false;
      }
    },
    logEveryStep: initialLogEveryStep,
    onLogEveryStepChange: (enabled) => {
      logEveryStepEnabled = enabled;
      physics.setDebugLogEveryFrame(enabled);
      persistLogEveryStep(enabled);
    },
    onStepOnce: () => {
      if (!simulationPaused || gpuValidationSubmissionHalted) return;
      pendingSimulationSteps = Math.min(pendingSimulationSteps + 1, 32);
    },
    onResetSimulation: () => {
      requestSimulationReset();
    },
    emitOneLabel: activeRuntime.shootProjectileOverride ? 'Fire ball' : 'Emit 1',
    emitTenLabel: activeRuntime.shootProjectileOverride ? 'Fire 10 balls' : 'Emit 10',
    onEmitRequested: (count) => {
      if (gpuValidationSubmissionHalted) return;
      const requestedCount = Math.max(0, Math.floor(count));
      if (activeRuntime.shootProjectileOverride) {
        // Coliseum owns a dedicated full-accuracy sphere projectile pool. The
        // generic BoxSpawner is intentionally only one slot there, so routing
        // the visible action buttons through it made Emit 10 silently useless
        // and did not fire the projectile the scene advertises.
        pendingImmediateShots = Math.min(64, pendingImmediateShots + requestedCount);
        presentationDirty = true;
        return;
      }
      // Body creation writes the same storage buffers consumed by physics.
      // Coalesce UI requests and apply them at a physics+render submission
      // boundary instead of mutating the world from an input callback.
      pendingManualEmitCount = Math.min(65535, pendingManualEmitCount + requestedCount);
      presentationDirty = true;
    },
    timeScale,
    onTimeScaleChange: (value) => {
      timeScale = Math.max(0.05, Math.min(2.0, value));
    },
    friction: physics.getAvbdFriction(),
    onFrictionChange: (friction) => physics.setAvbdFriction(friction),
    dualUpdateBeta: initialDualUpdateBeta,
    getDualUpdateBeta: () => physics.getAvbdDualUpdateBeta(),
    onDualUpdateBetaChange: (beta) => {
      physics.setAvbdLinearDualUpdateBeta(beta);
      persistDualUpdateBeta(beta);
      logAvbdConfig('linear-beta-change');
    },
    angularDualUpdateBeta: initialAngularDualUpdateBeta,
    getAngularDualUpdateBeta: () => physics.getAvbdAngularDualUpdateBeta(),
    onAngularDualUpdateBetaChange: (beta) => {
      physics.setAvbdAngularDualUpdateBeta(beta);
      persistAngularDualUpdateBeta(beta);
      logAvbdConfig('angular-beta-change');
    },
    regularizationAlpha: initialRegularizationAlpha,
    getRegularizationAlpha: () => physics.getAvbdRegularizationAlpha(),
    onRegularizationAlphaChange: (alpha) => {
      physics.setAvbdRegularizationAlpha(alpha);
      persistRegularizationAlpha(alpha);
      logAvbdConfig('alpha-change');
    },
    penaltyDecayGamma: initialPenaltyDecayGamma,
    getPenaltyDecayGamma: () => physics.getAvbdPenaltyDecayGamma(),
    onPenaltyDecayGammaChange: (gamma) => {
      physics.setAvbdPenaltyDecayGamma(gamma);
      persistPenaltyDecayGamma(gamma);
      logAvbdConfig('gamma-change');
    },
    solverIterations: initialSolverIterations,
    getSolverIterations: () => physics.getSolverIterations(),
    onSolverIterationsChange: (iterations) => {
      physics.setSolverIterations(iterations);
      persistSolverIterations(iterations);
      logAvbdConfig('solver-iterations-change');
    },
    stackPreset,
    onStackPresetChange: (preset) => {
      if (preset !== stackPreset) {
        reloadWithStackPreset(preset);
      }
    },
    defaults: panelDefaults,
  });
  fps = new FPSController(ctx.camera, ctx.renderer.domElement, {
    eyeHeight: playerEyeOffset,
    groundY: 0,
    moveSpeed: 7.0,
    jumpVelocity: 5.4,
    gravity: 16.0,
    physicsBodyMode: false,
    freeFlyMode: true,
  });
  fps.setSceneStatusText(activeRuntime.sceneStatusText?.() ?? null);
  (window as WebphysDebugWindow).__WEBPHYS_CAMERA_DEBUG__ = {
    capture: () => fps.captureState(),
    isLocked: () => fps.isLocked(),
  };
  const publishCameraProbe = (): void => {
    const state = fps.captureState();
    document.documentElement.dataset.cameraMode = fps.getMovementMode();
    document.documentElement.dataset.cameraLocked = String(fps.isLocked());
    document.documentElement.dataset.cameraPosition = state.position.map((value) => value.toFixed(4)).join(',');
    document.documentElement.dataset.cameraQuaternion = state.quaternion.map((value) => value.toFixed(5)).join(',');
  };
  publishCameraProbe();
  const desiredMoveVelocity = new THREE.Vector3();

  const isClothBoxesPreset = stackPreset === 'cloth-boxes-demo';
  const isColiseumPreset = stackPreset === 'coliseum-50k' || stackPreset === 'coliseum-64k';
  const isDominoPreset = stackPreset === 'dominoes-demo' || stackPreset === 'dominoes-advanced-demo';
  const fireConfig = {
    averageSpeed: isClothBoxesPreset ? 10.0 : (isColiseumPreset ? 40.0 : (isDominoPreset ? 12.0 : 18.0)),
    speedJitter: isClothBoxesPreset ? 2.0 : (isColiseumPreset ? 6.0 : (isDominoPreset ? 1.5 : 4.0)),
    angularJitter: isClothBoxesPreset ? 4.0 : (isColiseumPreset ? 5.0 : (isDominoPreset ? 2.0 : 8.0)),
    shotsPerSecond: 28.0,
    muzzleOffset: 1.1,
  };
  const fireOrigin = new THREE.Vector3();
  const fireDirection = new THREE.Vector3();
  let triggerHeld = false;
  let fireAccumulator = 0.0;
  let projectileShootAttempts = 0;
  let projectileShootSuccesses = 0;
  let projectileShootRejections = 0;
  const heldFireIntervalSeconds = isColiseumPreset ? 2.0 : (1.0 / fireConfig.shotsPerSecond);

  applyGpuValidationSimulationHalt = (): void => {
    simulationPaused = true;
    pendingSimulationSteps = 0;
    pendingManualStepSceneUpdateApplied = false;
    queuedSimulationDt = 0.0;
    pendingManualEmitCount = 0;
    pendingImmediateShots = 0;
    triggerHeld = false;
    fireAccumulator = 0.0;
    activeRuntime.spawner.setEmitter({ enabled: false });
    document.documentElement.dataset.physicsValidationHaltApplied = 'true';
    document.documentElement.dataset.physicsValidationHaltReason = gpuValidationHaltReason;
  };
  if (gpuValidationSubmissionHalted) applyGpuValidationSimulationHalt();

  ctx.renderer.domElement.addEventListener('contextmenu', (event) => {
    event.preventDefault();
    if (fps.isLocked()) fps.unlockPointer();
  });

  window.addEventListener('mousedown', (event) => {
    if (event.button === 2) {
      if (fps.isLocked()) {
        event.preventDefault();
        fps.unlockPointer();
      }
      return;
    }
    if (event.button !== 0) return;
    if (gpuValidationSubmissionHalted) return;
    if (!fps.isLocked()) {
      if (event.target === ctx.renderer.domElement) {
        fps.lockPointer();
      }
      return;
    }
    triggerHeld = true;
    fireAccumulator = 0.0;
    if (isColiseumPreset && !simulationPaused) {
      pendingImmediateShots = Math.min(64, pendingImmediateShots + 1);
    }
  });

  window.addEventListener('mouseup', (event) => {
    if (event.button !== 0) return;
    triggerHeld = false;
  });

  document.addEventListener('pointerlockchange', () => {
    if (fps.isLocked()) return;
    triggerHeld = false;
    fireAccumulator = 0.0;
  });

  const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
  };

  window.addEventListener('keydown', (event) => {
    if (event.code !== 'KeyR' || event.repeat) return;
    if (isTypingTarget(event.target)) return;
    event.preventDefault();
    requestSimulationReset();
  });

  const shoot = (): void => {
    presentationDirty = true;
    fps.getShootOrigin(fireOrigin, fireConfig.muzzleOffset);
    fps.getLookDirection(fireDirection);
    const shootConfig: ProjectileShootConfig = {
      position: [fireOrigin.x, fireOrigin.y, fireOrigin.z],
      direction: [fireDirection.x, fireDirection.y, fireDirection.z],
      averageSpeed: fireConfig.averageSpeed,
      speedJitter: fireConfig.speedJitter,
      angularJitter: fireConfig.angularJitter,
    };
    const accepted = activeRuntime.shootProjectileOverride
      ? activeRuntime.shootProjectileOverride(shootConfig)
      : activeRuntime.spawner.shoot(shootConfig);
    projectileShootAttempts++;
    if (accepted) projectileShootSuccesses++;
    else projectileShootRejections++;
    document.documentElement.dataset.projectileShootAttempts = String(projectileShootAttempts);
    document.documentElement.dataset.projectileShootSuccesses = String(projectileShootSuccesses);
    document.documentElement.dataset.projectileShootRejections = String(projectileShootRejections);
  };

  // Render loop
  const timer = new THREE.Timer();
  timer.connect(document);
  timer.update();
  let bridgeMiniDiagnosticsInFlight = false;
  let lastBridgeMiniDiagnosticsFrame = -1;
  let carouselDiagnosticsInFlight = false;
  let lastCarouselDiagnosticsFrame = -1;

  const sampleBridgeMiniDiagnostics = async (): Promise<void> => {
    const bridgeMiniDiagnostics = activeRuntime.bridgeMiniDiagnostics;
    if (!bridgeMiniDiagnostics || bridgeMiniDiagnosticsInFlight) return;
    if (physics.stats.frameCount < 16) return;

    bridgeMiniDiagnosticsInFlight = true;
    try {
      const [bodyStates, jointStates] = await Promise.all([
        physics.readRigidBodyStatesAsync(ctx.renderer),
        physics.readJointStatesAsync(
          ctx.renderer,
          bridgeMiniDiagnostics.joints.map((joint) => joint.joint),
        ),
      ]);
      if (!bodyStates || !jointStates) return;

      const statesByBody = new Map(bodyStates.map((state) => [state.body, state] as const));
      const jointStatesById = new Map(jointStates.map((joint) => [joint.joint, joint] as const));
      const centerState = statesByBody.get(bridgeMiniDiagnostics.centerBody);
      if (!centerState) return;

      const centerEuler = new THREE.Euler().setFromQuaternion(
        new THREE.Quaternion(
          centerState.quaternion[0],
          centerState.quaternion[1],
          centerState.quaternion[2],
          centerState.quaternion[3],
        ),
        'XYZ',
      );
      const centerEulerDeg: [number, number, number] = [
        THREE.MathUtils.radToDeg(centerEuler.x),
        THREE.MathUtils.radToDeg(centerEuler.y),
        THREE.MathUtils.radToDeg(centerEuler.z),
      ];

      const jointSummaries = bridgeMiniDiagnostics.joints.map((joint) => {
        const jointState = jointStatesById.get(joint.joint);
        const summary: Record<string, unknown> = {
          label: joint.label,
          joint: joint.joint,
          type: jointState?.type ?? 'missing',
          anchorError: jointAnchorError(statesByBody, joint),
          penaltyLin: jointState?.penaltyLin ?? [0.0, 0.0, 0.0],
          lambdaLin: jointState?.lambdaLin ?? [0.0, 0.0, 0.0],
          c0Lin: jointState?.c0Lin ?? [0.0, 0.0, 0.0],
        };

        if (jointState?.type === 'fixed') {
          const qA = jointState.bodyA === null
            ? [0.0, 0.0, 0.0, 1.0] as [number, number, number, number]
            : (statesByBody.get(jointState.bodyA)?.quaternion ?? [0.0, 0.0, 0.0, 1.0] as [number, number, number, number]);
          const qB = statesByBody.get(jointState.bodyB)?.quaternion ?? [0.0, 0.0, 0.0, 1.0] as [number, number, number, number];
          summary.angularErrorCurrent = currentJointAngularError(qA, qB, jointState.restRelative);
          summary.angularErrorDemo3d = demo3dStyleJointAngularError(qA, qB);
          summary.penaltyAng = jointState.penaltyAng;
          summary.lambdaAng = jointState.lambdaAng;
          summary.c0Ang = jointState.c0Ang;
        }

        return summary;
      });

      const fixedJointSummaries = jointSummaries.filter((joint) => joint.type === 'fixed');
      const snapshot = {
        preset: bridgeMiniDiagnostics.preset,
        frame: physics.stats.frameCount,
        centerBody: bridgeMiniDiagnostics.centerBody,
        centerPosition: centerState.position,
        centerEulerDeg,
        centerAngularVelocity: centerState.angularVelocity,
        centerAngularSpeed: vectorMagnitude3(centerState.angularVelocity),
        maxAnchorError: Math.max(...jointSummaries.map((joint) => Number(joint.anchorError ?? 0.0))),
        maxCurrentAngularError: fixedJointSummaries.length > 0
          ? Math.max(...fixedJointSummaries.map((joint) => vectorMagnitude3(joint.angularErrorCurrent as [number, number, number])))
          : 0.0,
        maxDemo3dAngularError: fixedJointSummaries.length > 0
          ? Math.max(...fixedJointSummaries.map((joint) => vectorMagnitude3(joint.angularErrorDemo3d as [number, number, number])))
          : 0.0,
        joints: jointSummaries,
      };

      (window as typeof window & { __WEBPHYS_BRIDGE_MINI_DEBUG__?: unknown }).__WEBPHYS_BRIDGE_MINI_DEBUG__ = snapshot;

      if (logEveryStepEnabled || physics.stats.frameCount % 60 === 0) {
        const jointText = jointSummaries.map((joint) => {
          if (joint.type === 'fixed') {
            return `${String(joint.label)} err=${Number(joint.anchorError ?? 0.0).toExponential(2)} angGpu=${vectorMagnitude3(joint.angularErrorCurrent as [number, number, number]).toExponential(2)} angDemo3d=${vectorMagnitude3(joint.angularErrorDemo3d as [number, number, number]).toExponential(2)} kAng=${formatTriple(joint.penaltyAng as [number, number, number], 2)}`;
          }
          return `${String(joint.label)} err=${Number(joint.anchorError ?? 0.0).toExponential(2)} kLin=${formatTriple(joint.penaltyLin as [number, number, number], 2)}`;
        }).join(' | ');
        console.info(
          `[BridgeMini:${bridgeMiniDiagnostics.preset}] frame=${physics.stats.frameCount} centerEulerDeg=${formatTriple(centerEulerDeg, 2)} angVel=${formatTriple(centerState.angularVelocity, 2)} maxAnchorErr=${snapshot.maxAnchorError.toExponential(2)} ${jointText}`,
        );
      }
    } catch (error) {
      console.warn('[BridgeMini] diagnostics readback failed:', error);
    } finally {
      bridgeMiniDiagnosticsInFlight = false;
    }
  };

  const sampleCarouselDiagnostics = async (): Promise<void> => {
    const carouselDiagnostics = activeRuntime.carouselDiagnostics;
    if (!carouselDiagnostics || carouselDiagnosticsInFlight) return;
    if (physics.stats.frameCount < 16) return;

    carouselDiagnosticsInFlight = true;
    try {
      const [bodyStates, jointStates] = await Promise.all([
        physics.readRigidBodyStatesAsync(ctx.renderer),
        physics.readJointStatesAsync(
          ctx.renderer,
          carouselDiagnostics.joints.map((joint) => joint.joint),
        ),
      ]);
      if (!bodyStates || !jointStates) return;

      const statesByBody = new Map(bodyStates.map((state) => [state.body, state] as const));
      const jointStatesById = new Map(jointStates.map((joint) => [joint.joint, joint] as const));

      const jointSummaries = carouselDiagnostics.joints.map((joint) => {
        const jointState = jointStatesById.get(joint.joint);
        return {
          label: joint.label,
          joint: joint.joint,
          anchorError: jointAnchorError(statesByBody, joint),
          c0Lin: jointState?.c0Lin ?? [0.0, 0.0, 0.0],
          penaltyLin: jointState?.penaltyLin ?? [0.0, 0.0, 0.0],
          lambdaLin: jointState?.lambdaLin ?? [0.0, 0.0, 0.0],
        };
      });

      const snapshot = {
        frame: physics.stats.frameCount,
        maxTopAnchorError: Math.max(...jointSummaries.map((joint) => Number(joint.anchorError ?? 0.0))),
        joints: jointSummaries,
      };

      (window as typeof window & { __WEBPHYS_CAROUSEL_DEBUG__?: unknown }).__WEBPHYS_CAROUSEL_DEBUG__ = snapshot;

      if (logEveryStepEnabled || physics.stats.frameCount % 60 === 0) {
        const jointText = jointSummaries
          .slice(0, 8)
          .map((joint) =>
            `${joint.label} err=${Number(joint.anchorError).toExponential(2)} c0=${formatTriple(joint.c0Lin as [number, number, number], 2)}`
          )
          .join(' | ');
        console.info(
          `[Carousel] frame=${physics.stats.frameCount} maxTopErr=${snapshot.maxTopAnchorError.toExponential(2)} ${jointText}`,
        );
      }
    } catch (error) {
      console.warn('[Carousel] diagnostics readback failed:', error);
    } finally {
      carouselDiagnosticsInFlight = false;
    }
  };

  ctx.renderer.setAnimationLoop(() => {
    const animationFrameNow = performance.now();
    updateGpuQueueTelemetry(animationFrameNow, getMaxGpuFramesInFlight());
    if (gpuRecoveryScheduled) return;
    if (gpuValidationSubmissionHalted) applyGpuValidationSimulationHalt?.();

    timer.update();
    const realDt = Math.min(timer.getDelta(), 0.05);
    const dt = Math.min(realDt * timeScale, 0.05);
    const simulationFrame = physics.stats.frameCount;
    if (!simulationPaused && !resetRequested) {
      // Preserve elapsed simulation time across submission-gated rAF ticks,
      // while retaining the engine's one-step catch-up clamp.
      queuedSimulationDt = Math.min(queuedSimulationDt + dt, 0.05);
    }

    fps.update(dt);
    fps.setSceneStatusText(activeRuntime.sceneStatusText?.() ?? null);
    fps.getDesiredMoveVelocity(desiredMoveVelocity);
    fps.consumeJumpRequest();
    if (physics.stats.pairDispatchTruncated) {
      pendingSimulationSteps = 0;
      pendingManualStepSceneUpdateApplied = false;
      pendingManualEmitCount = 0;
      pendingImmediateShots = 0;
      triggerHeld = false;
      fireAccumulator = 0.0;
      if (!capacitySpawningStopped) {
        capacitySpawningStopped = true;
        activeRuntime.spawner.setEmitter({ enabled: false });
        document.documentElement.dataset.physicsCapacitySpawningStopped = 'true';
      }
    }
    // Capture before reset/readback/physics/render code so every submit made
    // by this animation callback belongs to the same whole-frame audit.
    const frameSubmitCursor = frameSubmitObserver.available
      ? frameSubmitObserver.cursor
      : -1;
    // A queued resize intentionally stops new submissions until the old queue
    // retires. Apply all renderer-owned dimensions together, then present at
    // the new size in this same animation callback.
    const resizeAppliedThisFrame = applyPendingPresentationResize();
    const gtaoTargetMutationAppliedThisFrame = applyPendingGtaoTargetMutation();
    if (
      resetRequested
      && !gpuValidationSubmissionHalted
      && gpuFramesInFlight === 0
      && !capacityDiagnosticsInFlight
      && !bridgeMiniDiagnosticsInFlight
      && !carouselDiagnosticsInFlight
      && !rendererPipelineMutationInProgress
    ) {
      resetSimulation();
      resetRequested = false;
    }
    const maxGpuFramesInFlight = getMaxGpuFramesInFlight();
    // An uncaptured validation error invalidates the command graph itself.
    // Keep the rAF/DOM loop alive for the visible diagnosis, but never render,
    // reset, read back, or submit another GPU command against that graph.
    const gpuSubmissionAllowed = !gpuValidationSubmissionHalted
      && !resetRequested
      && !rendererPipelineMutationInProgress
      && pendingPresentationResize === null
      && !(gtaoTargetMutation && performance.now() >= gtaoTargetMutation.notBefore);
    const cameraChanged = !ctx.camera.position.equals(lastPresentedCameraPosition)
      || !ctx.camera.quaternion.equals(lastPresentedCameraQuaternion);
    const presentationCadenceRequired = !lastRenderOnDemandIdle
      || presentationDirty
      || forcedPresentationFrames > 0
      || cameraChanged;
    // Once both swapchain presentations are outstanding, allow at most one
    // physics-only frame ahead. This preserves a little compute overlap while
    // preventing a deep 8-frame simulation queue from delaying the next camera
    // update or visible frame.
    const presentationBackpressured = gpuPresentationsInFlight >= MAX_PRESENTATIONS_IN_FLIGHT
      && presentationCadenceRequired
      && gpuFramesInFlight > gpuPresentationsInFlight;
    const canSubmitPhysicsFrame = gpuSubmissionAllowed
      && !resizeAppliedThisFrame
      && !gtaoTargetMutationAppliedThisFrame
      && !presentationBackpressured
      && gpuFramesInFlight < maxGpuFramesInFlight;
    // Reserve one bounded presentation-only slot. If physics fills its frame
    // ring, camera/UI rendering may still advance once instead of appearing
    // frozen, but repeated rAFs cannot grow an unbounded GPU queue backlog.
    const canSubmitPresentationFrame = gpuSubmissionAllowed
      && gpuFramesInFlight < maxGpuFramesInFlight + 1
      && gpuPresentationsInFlight < MAX_PRESENTATIONS_IN_FLIGHT;
    const canSubmitGpuFrame = canSubmitPhysicsFrame || canSubmitPresentationFrame;
    let presentationSkippedCandidate = false;
    let presentedThisFrame = false;
    if (canSubmitGpuFrame) {
      gpuPassTimer.beginFrame();
      overlay.begin();

      const submittedSimulationDt = !physicsStartupReady
        ? 0.0
        : simulationPaused
        ? (pendingSimulationSteps > 0 ? physics.config.deltaTime : 0.0)
        : queuedSimulationDt;
      if (physicsStartupReady && canSubmitPhysicsFrame && !simulationPaused) {
        queuedSimulationDt = 0.0;
      }

      if (physicsStartupReady && canSubmitPhysicsFrame) {
      if (pendingManualEmitCount > 0) {
        activeRuntime.spawner.emit(pendingManualEmitCount);
        pendingManualEmitCount = 0;
      }
      while (pendingImmediateShots > 0) {
        shoot();
        pendingImmediateShots--;
      }

      if (
        perfEmitConfig
        && !perfEmitTriggered
        && !capacitySpawningStopped
        && !simulationPaused
        && simulationFrame >= perfEmitConfig.frame
      ) {
        const emitted = activeRuntime.spawner.emit(perfEmitConfig.count);
        perfEmitTriggered = true;
        console.info(
          `[Perf Emit] frame=${simulationFrame} requested=${perfEmitConfig.count} emitted=${emitted}`,
        );
      }
      activeRuntime.spawner.update(simulationPaused ? 0.0 : submittedSimulationDt);

      if (!simulationPaused && fps.isLocked() && triggerHeld) {
        fireAccumulator += submittedSimulationDt;
        while (fireAccumulator >= heldFireIntervalSeconds) {
          shoot();
          fireAccumulator -= heldFireIntervalSeconds;
        }
      } else {
        fireAccumulator = 0.0;
      }

      if (simulationPaused) {
        if (pendingSimulationSteps > 0) {
          if (!pendingManualStepSceneUpdateApplied) {
            for (const updateScene of activeRuntime.sceneUpdateCallbacks) {
              updateScene(submittedSimulationDt);
            }
            pendingManualStepSceneUpdateApplied = true;
          }
          const simulationStepsBefore = physics.stats.simulationStepCount;
          physics.step(submittedSimulationDt, ctx.renderer);
          // Large scenes need one or more maintenance calls to publish their
          // initial LBVH/candidate stream. Those calls increment frameCount but
          // deliberately do not complete a fixed step. Retain the manual-step
          // token until simulationStepCount actually advances; otherwise a
          // paused Coliseum consumes its only request on Frame 1 and can never
          // revisit the now-ready broadphase without being unpaused.
          if (
            physics.stats.simulationStepCount > simulationStepsBefore
          ) {
            pendingSimulationSteps--;
            pendingManualStepSceneUpdateApplied = false;
          } else if (physics.stats.pairDispatchTruncated) {
            pendingSimulationSteps = 0;
            pendingManualStepSceneUpdateApplied = false;
          }
        }
      } else {
        for (const updateScene of activeRuntime.sceneUpdateCallbacks) {
          updateScene(submittedSimulationDt);
        }
        physics.step(submittedSimulationDt, ctx.renderer);
      }
      if (
        startupFirstTrueSimulationStepMs === null
        && physics.stats.simulationStepCount > 0
      ) {
        startupFirstTrueSimulationStepMs = performance.now() - startupStartedAt;
        startupTrueStepObserved = true;
        document.documentElement.dataset.startupFirstTrueSimulationStepMs =
          startupFirstTrueSimulationStepMs.toFixed(3);
      }

      activeRuntime.spawner.syncVisuals(physics);
      for (const trackedVisuals of activeRuntime.trackedVisualSets) {
        trackedVisuals.syncVisuals(physics);
      }
      for (const trackedSpringVisuals of activeRuntime.trackedSpringVisualSets) {
        trackedSpringVisuals.syncVisuals(physics);
      }
      for (const syncedVisuals of activeRuntime.syncedSceneVisuals) {
        syncedVisuals.syncVisuals(physics);
      }
      activeRuntime.contactPointOverlay.requestUpdate(physics);
      if (activeRuntime.bridgeMiniDiagnostics && debugBehaviorEnabled) {
        const sampleEvery = logEveryStepEnabled ? 1 : 15;
        const frame = physics.stats.frameCount;
        if (frame > 0 && frame !== lastBridgeMiniDiagnosticsFrame && frame % sampleEvery === 0) {
          lastBridgeMiniDiagnosticsFrame = frame;
          void sampleBridgeMiniDiagnostics();
        }
      }
      if (activeRuntime.carouselDiagnostics && debugBehaviorEnabled) {
        const sampleEvery = logEveryStepEnabled ? 1 : 15;
        const frame = physics.stats.frameCount;
        if (frame > 0 && frame !== lastCarouselDiagnosticsFrame && frame % sampleEvery === 0) {
          lastCarouselDiagnosticsFrame = frame;
          void sampleCarouselDiagnostics();
        }
      }
      }

      const zeroWork = physics.getSleepingZeroWorkDiagnostics();
      // Logical zero-work steps advance the public step counter without
      // changing any GPU pose. Subtract them so a fully sleeping 64K world can
      // retain its cached shadow and render-on-demand path instead of being
      // dirtied at 60 Hz forever.
      const currentShadowSimulationStepCount = Math.max(
        0,
        physics.stats.simulationStepCount - physics.stats.sleepingZeroWorkSteps,
      );
      // The authoritative allocator count changes synchronously on paused
      // emits; stats.bodyCount is published later and could miss that topology
      // change for one presentation.
      const currentShadowBodyCount = physics.getBodyCount();
      const callbackDrivenPoseChanged = physicsStartupReady
        && canSubmitPhysicsFrame
        && submittedSimulationDt > 0
        && activeRuntime.sceneUpdateCallbacks.length > 0;
      if (
        currentShadowSimulationStepCount !== lastShadowSimulationStepCount
        || currentShadowBodyCount !== lastShadowBodyCount
        || callbackDrivenPoseChanged
      ) {
        shadowSceneDirty = true;
        lastShadowSimulationStepCount = currentShadowSimulationStepCount;
        lastShadowBodyCount = currentShadowBodyCount;
        document.documentElement.dataset.shadowPoseRevision = String(
          currentShadowSimulationStepCount,
        );
      }
      presentationSkippedCandidate = isVeryLargeScene
        && frameSubmitObserver.available
        && zeroWork?.eligible === true
        && zeroWork.completedFrameSubmitted
        && zeroWork.confirmedFrameId === zeroWork.completedFrameId
        && forcedPresentationFrames === 0
        && !presentationDirty
        && !cameraChanged
        // Drain the last scheduled shadow refresh before an island enters the
        // render-on-demand zero-work state. Otherwise the final moving pose can
        // remain out of sync with the cached depth map indefinitely.
        && (!referenceLighting.shadowsEnabled || !shadowSceneDirty)
        && !showContactPointsEnabled
        && !gpuPassTimer.isEnabled()
        && !resetRequested
        && !physics.stats.pairDispatchTruncated
        && !capacityDiagnosticsInFlight
        && !bridgeMiniDiagnosticsInFlight
        && !carouselDiagnosticsInFlight;
      if (
        canSubmitPresentationFrame
        && !presentationSkippedCandidate
        && presentationPipelinesReady
      ) {
        const shadowUpdated = referenceLighting.updateShadowSchedule({
          sceneDirty: shadowSceneDirty,
          force: presentationRenderFrames === 0,
        });
        if (shadowUpdated) {
          shadowSceneDirty = false;
          shadowUpdateCount++;
          lastShadowPresentedPoseRevision = currentShadowSimulationStepCount;
          document.documentElement.dataset.shadowUpdateCount = String(shadowUpdateCount);
          document.documentElement.dataset.shadowPresentedPoseRevision = String(
            lastShadowPresentedPoseRevision,
          );
        }
        document.documentElement.dataset.shadowLagSteps = String(Math.max(
          0,
          currentShadowSimulationStepCount - lastShadowPresentedPoseRevision,
        ));
        if (gtao?.enabled) gtao.render();
        else ctx.renderer.render(ctx.scene, ctx.camera);
        presentedThisFrame = true;
        presentationRenderFrames++;
        const presentedAt = performance.now();
        if (lastPresentationTimestamp > 0) {
          const instantFps = 1000 / Math.max(presentedAt - lastPresentationTimestamp, 0.25);
          if (Number.isFinite(instantFps)) {
            presentationFpsEma = THREE.MathUtils.lerp(
              presentationFpsEma,
              THREE.MathUtils.clamp(instantFps, 1, 240),
              0.08,
            );
          }
        }
        lastPresentationTimestamp = presentedAt;
        if (
          lastPresentationTelemetryTimestamp === 0
          || presentedAt - lastPresentationTelemetryTimestamp >= 250
        ) {
          lastPresentationTelemetryTimestamp = presentedAt;
          document.documentElement.dataset.presentationFpsEma = presentationFpsEma.toFixed(2);
        }
        if (startupFirstRenderMs === null) {
          startupFirstRenderMs = performance.now() - startupStartedAt;
          document.documentElement.dataset.startupTimeToFirstRenderMs =
            startupFirstRenderMs.toFixed(3);
        }
        lastPresentedCameraPosition.copy(ctx.camera.position);
        lastPresentedCameraQuaternion.copy(ctx.camera.quaternion);
        presentationDirty = false;
        if (forcedPresentationFrames > 0) forcedPresentationFrames--;
      }
      gpuPassTimer.endFrame();

      if (physics.stats.frameCount % 5 === 0) {
        publishCameraProbe();
      }
    } else if (!rendererPipelineMutationInProgress) {
      skippedGpuSubmissionFrames++;
    }
    overlay.end(presentedThisFrame);

    if (
      canSubmitPhysicsFrame
      && physics.stats.pairDispatchTruncated
      && !capacityDiagnosticsCaptured
      && !capacityDiagnosticsInFlight
    ) {
      capacityDiagnosticsInFlight = true;
      const diagnosticsGeneration = capacityDiagnosticsGeneration;
      void Promise.all([
        physics.readContactCapacityLossAsync(ctx.renderer),
        physics.readPersistentAllocatorStateAsync(ctx.renderer),
      ]).then(([contact, persistentAllocator]) => {
        if (diagnosticsGeneration !== capacityDiagnosticsGeneration) return;
        const broadphase = physics.getBroadphaseCapacityDiagnostics();
        document.documentElement.dataset.physicsCapacityDiagnostics = JSON.stringify({
          broadphase,
          contact,
          persistentAllocator,
        });
        capacityDiagnosticsCaptured = true;
      }).catch((error: unknown) => {
        if (diagnosticsGeneration === capacityDiagnosticsGeneration) {
          console.warn('Capacity diagnostics readback failed:', error);
        }
      }).finally(() => {
        if (diagnosticsGeneration === capacityDiagnosticsGeneration) {
          capacityDiagnosticsInFlight = false;
        }
      });
    }

    if (canSubmitGpuFrame) {
      const submitObservationAvailable = frameSubmitCursor >= 0 && frameSubmitObserver.available;
      const submitCursorAfterFrame = submitObservationAvailable ? frameSubmitObserver.cursor : -1;
      lastFrameObservedSubmitCount = submitObservationAvailable
        ? Math.max(0, submitCursorAfterFrame - frameSubmitCursor)
        : -1;
      const wholeFrameGpuIdle = presentationSkippedCandidate
        && submitObservationAvailable
        && lastFrameObservedSubmitCount === 0
        && !capacityDiagnosticsInFlight;
      const frameProvenGpuIdle = submitObservationAvailable
        && lastFrameObservedSubmitCount === 0
        && !capacityDiagnosticsInFlight;
      if (presentationSkippedCandidate) {
        if (wholeFrameGpuIdle) {
          renderOnDemandSkippedFrames++;
        } else {
          renderOnDemandUnexpectedSubmitFrames++;
        }
      }
      lastRenderOnDemandIdle = wholeFrameGpuIdle;

      // Any cursor-proven zero-submit frame may omit its queue fence, including
      // startup frames while render/physics pipelines are intentionally gated.
      // The old skip-only rule registered empty onSubmittedWorkDone fences and
      // let them consume the same backpressure ring as real physics. If the
      // observer is unavailable/replaced, fail safe and retain the fence.
      if (!frameProvenGpuIdle) registerGpuSubmissionFence(presentedThisFrame);
    } else {
      lastRenderOnDemandIdle = false;
    }

    const telemetryNow = performance.now();
    updateGpuQueueTelemetry(telemetryNow, maxGpuFramesInFlight);
    if (telemetryNow - lastPhysicsTelemetryPublishedAt >= 250.0) {
      lastPhysicsTelemetryPublishedAt = telemetryNow;
      document.documentElement.dataset.physicsBroadPhaseReady = String(physics.stats.broadPhaseReady);
      document.documentElement.dataset.physicsCandidatePairsEnabled = String(physics.stats.candidatePairsEnabled);
      document.documentElement.dataset.physicsPairDispatchTruncated = String(physics.stats.pairDispatchTruncated);
      document.documentElement.dataset.physicsBodyCount = String(physics.stats.bodyCount);
      document.documentElement.dataset.physicsFrameCount = String(physics.stats.frameCount);
      document.documentElement.dataset.physicsSimulationStepCount = String(physics.stats.simulationStepCount);
      const cpuOptimizationTelemetry = physics.getCpuOptimizationTelemetry();
      document.documentElement.dataset.cpuMutationBatchCompletions = String(
        cpuOptimizationTelemetry.mutationBatchCompletions,
      );
      document.documentElement.dataset.cpuRendererPublishRequestsBatched = String(
        cpuOptimizationTelemetry.rendererPublishRequestsBatched,
      );
      document.documentElement.dataset.cpuRendererPublishFlushes = String(
        cpuOptimizationTelemetry.rendererPublishFlushes,
      );
      document.documentElement.dataset.cpuNativeCommandCacheHits = String(
        cpuOptimizationTelemetry.nativeCommandCacheHits,
      );
      document.documentElement.dataset.cpuNativeCommandCacheMisses = String(
        cpuOptimizationTelemetry.nativeCommandCacheMisses,
      );
      document.documentElement.dataset.cpuNativePreparedCommandCapacity = String(
        cpuOptimizationTelemetry.nativePreparedCommandCapacity,
      );
      document.documentElement.dataset.avbdProfile = requestedAvbdProfile.id;
      document.documentElement.dataset.avbdStoredTuningLoaded = String(loadStoredAvbdTuning);
      document.documentElement.dataset.avbdScenePhysicsExtension =
        stackPreset === 'newtons-cradles-demo' ? 'restitution' : 'none';
      document.documentElement.dataset.avbdExecutionSchedule =
        requestedAvbdProfile.executionPolicy?.schedule ?? 'scene-production-policy';
      document.documentElement.dataset.avbdSerialBodyOrderParity = String(
        requestedAvbdProfile.executionPolicy?.serialBodyOrderParity ?? false,
      );
      document.documentElement.dataset.avbdBetaLinear = String(physics.getAvbdDualUpdateBeta());
      document.documentElement.dataset.avbdBetaAngular = String(
        physics.getAvbdAngularDualUpdateBeta(),
      );
      document.documentElement.dataset.avbdRegularizationAlpha = String(
        physics.getAvbdRegularizationAlpha(),
      );
      document.documentElement.dataset.avbdPenaltyDecayGamma = String(
        physics.getAvbdPenaltyDecayGamma(),
      );
      const adaptiveIterationsConfigured = physics.getAvbdAdaptiveIterationsConfigured();
      const adaptiveSelectedLast = physics.getAvbdAdaptiveSelectedLast();
      const completedSolveAvailable = physics.stats.simulationStepCount > 0;
      document.documentElement.dataset.avbdAdaptiveIterationsConfigured = String(
        adaptiveIterationsConfigured,
      );
      document.documentElement.dataset.avbdAdaptiveMinIterations = String(
        physics.getAvbdAdaptiveMinIterations(),
      );
      document.documentElement.dataset.avbdAdaptiveSelectedLast = String(
        adaptiveSelectedLast,
      );
      document.documentElement.dataset.avbdIterationPolicyConfigured =
        adaptiveIterationsConfigured
          ? `adaptive-min-${physics.getAvbdAdaptiveMinIterations()}`
          : 'fixed';
      document.documentElement.dataset.avbdIterationPolicyActual = !completedSolveAvailable
        ? 'pending'
        : adaptiveSelectedLast
        ? 'adaptive-selected'
        : adaptiveIterationsConfigured
        ? 'fixed-fallback'
        : 'fixed-configured';
      document.documentElement.dataset.avbdIterationsLast = String(
        physics.stats.avbdIterationsLast,
      );
      document.documentElement.dataset.avbdIterationsAverage =
        physics.stats.avbdIterationsAverage.toFixed(3);
      document.documentElement.dataset.avbdAdaptiveEarlyExits = String(
        physics.stats.avbdAdaptiveEarlyExits,
      );
      document.documentElement.dataset.renderOnDemandSkippedFrames = String(
        renderOnDemandSkippedFrames,
      );
      document.documentElement.dataset.renderOnDemandUnexpectedSubmitFrames = String(
        renderOnDemandUnexpectedSubmitFrames,
      );
      document.documentElement.dataset.renderOnDemandPresentedFrames = String(presentationRenderFrames);
      document.documentElement.dataset.renderOnDemandLastSubmitCount = String(lastFrameObservedSubmitCount);
      document.documentElement.dataset.renderOnDemandSubmitObserverAvailable = String(
        frameSubmitObserver.available,
      );
      document.documentElement.dataset.renderOnDemandIdle = String(lastRenderOnDemandIdle);
    }
    overlay.update(physics.stats, {
      profile: requestedAvbdProfile.id,
      substeps: physics.getSubsteps(),
      solverIterations: physics.getSolverIterations(),
      adaptiveIterationsConfigured: physics.getAvbdAdaptiveIterationsConfigured(),
      adaptiveMinIterations: physics.getAvbdAdaptiveMinIterations(),
      adaptiveSelectedLast: physics.getAvbdAdaptiveSelectedLast(),
      completedSolveAvailable: physics.stats.simulationStepCount > 0,
      iterationsLast: physics.stats.avbdIterationsLast,
      iterationsAverage: physics.stats.avbdIterationsAverage,
      adaptiveEarlyExits: physics.stats.avbdAdaptiveEarlyExits,
      alpha: physics.getAvbdRegularizationAlpha(),
      beta: physics.getAvbdDualUpdateBeta(),
      betaAngular: physics.getAvbdAngularDualUpdateBeta(),
      gamma: physics.getAvbdPenaltyDecayGamma(),
      kStart: physics.getAvbdPenaltyFloor(),
    });
    emitterPanel.update();
  });

  window.addEventListener('pagehide', (event) => {
    // Do not keep a hundreds-of-MiB WebGPU world alive in the back/forward
    // cache. That preserved device queue was the reason a 64k page could make
    // the next lightweight scene run at ~1 FPS.
    void event;
    shuttingDown = true;
    window.clearInterval(gpuQueueWatchdogTimer);
    ctx.renderer.setAnimationLoop(null);
    window.removeEventListener('resize', queuePresentationResize);
    // Renderer.compileAsync, PassNode compilation and physics prewarming are
    // uncancellable. Destroying their materials/device synchronously here is a
    // real use-after-dispose race. Stop new work immediately, then retire all
    // resources after every in-flight owner and the submitted GPU queue settle.
    // GPUDevice.destroy is the WebGPU cancellation boundary for a terminal
    // navigation: it rejects pending native work and releases the very large
    // 50K/64K allocations promptly. Keep the JS-side renderer/material objects
    // alive until their async owners observe the lost device and settle.
    ctx.device.destroy();
    const pendingShutdownWork = [
      presentationPrewarmPromise,
      physicsPrewarmPromise,
      gtaoCompilePromise,
      resetPresentationCompilePromise,
      gtaoQueuedAfterResetPromise,
    ].filter((promise): promise is Promise<void> => promise !== null);
    void (async () => {
      await Promise.allSettled(pendingShutdownWork);
      // A reset/GTAO continuation may have been published just before the
      // shutdown flag became visible; take one final dynamic barrier snapshot.
      await Promise.allSettled(
        [gtaoCompilePromise, resetPresentationCompilePromise, gtaoQueuedAfterResetPromise]
          .filter((promise): promise is Promise<void> => promise !== null),
      );
    })().finally(() => {
      const retiringGtao = gtao;
      gtao = null;
      retiringGtao?.dispose();
      activeRuntime.dispose();
      gpuPassTimer.dispose();
      physics.dispose();
      referenceLighting.dispose();
      ctx.renderer.dispose();
    });
  }, { once: true });
  window.addEventListener('pageshow', (event) => {
    // The cached page was intentionally torn down above; rebuild it cleanly if
    // the user navigates back instead of resuming a destroyed GPU device.
    if (event.persisted) {
      window.location.reload();
    } else {
      presentationDirty = true;
      forcedPresentationFrames = Math.max(forcedPresentationFrames, 2);
    }
  });
}

function loadAngularDualUpdateBeta(defaultValue: number): number {
  try {
    const stored = window.localStorage.getItem(ANGULAR_DUAL_UPDATE_BETA_STORAGE_KEY);
    if (stored === null) return defaultValue;
    const value = Number(stored);
    if (!Number.isFinite(value)) return defaultValue;
    return Math.max(0.0, value);
  } catch {
    return defaultValue;
  }
}

function persistAngularDualUpdateBeta(beta: number): void {
  try {
    window.localStorage.setItem(
      ANGULAR_DUAL_UPDATE_BETA_STORAGE_KEY,
      String(Math.max(0.0, beta)),
    );
  } catch {
    // Ignore storage errors (private mode / blocked storage).
  }
}

main().catch((err) => {
  document.body.innerHTML = `<pre style="color:red;padding:2em;font-size:14px">${err.message}\n\n${err.stack}</pre>`;
  console.error(err);
});
